import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { TruthManagement as TruthManagementService, AiSuggestMessage, AiSuggestResponse } from '../../services/truth-management';
import { DocumentModelService } from '../../services/document-model.service';
import { NotificationService } from '../../services/notification.service';
import { ModalService } from '../../services/modal.service';
import { CanComponentDeactivate } from '../../guards/can-deactivate.guard';
import { File, EvaluationFile } from '../../models/truth-management.model';
import { PageHeaderComponent } from '../page-header/page-header.component';

interface TrainingChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  displayText: string;
  statusType: 'ready' | 'needs_clarification' | 'cancelled' | null;
  mode: 'chat' | 'description';
  contextLabel: string;
  createdAt: Date;
  renderedText: string;
  isTyping: boolean;
  readyDetails: TrainingReadyDetailsView | null;
  readyDetailsList: TrainingReadyDetailsView[] | null;
}

interface TrainingSnipSelection {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface TrainingReadyDetailsView {
  rawResponse: string;
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
}

interface TrainingGenerateDraftPayload {
  documentModel: string;
  createdAt: number;
  returnUrl: string;
  autoCreate?: boolean;
  fields: TrainingGenerateDraftFieldPayload[];
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
}

interface TrainingGenerateDraftFieldPayload {
  fieldName: string;
  fieldType: string;
  feature: string;
  prompt: string;
  additionalPrompt: string;
  fieldSchema: string;
  computeSchema: string;
  externalKey: string;
}

interface TrainingEditedDetailPayload {
  field_name: string;
  field_type: string;
  feature: string;
  prompt: string;
  additional_prompt: string;
  field_schema: string;
  compute_schema: string;
  external_key: string;
}

type TrainingEditableDetailColumn = 'prompt' | 'additionalPrompt' | 'fieldSchema' | 'computeSchema';
type TrainingRequiredDetailColumn = 'fieldName' | 'fieldType' | 'feature' | 'prompt' | 'additionalPrompt' | 'fieldSchema' | 'computeSchema';

@Component({
  selector: 'app-prompt-tuning',
  standalone: true,
  imports: [CommonModule, FormsModule, PdfViewerModule, PageHeaderComponent],
  templateUrl: './prompt-tuning.html',
  styleUrls: ['./prompt-tuning.scss']
})
export class PromptTuningComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('contentGrid') contentGrid?: ElementRef<HTMLDivElement>;
  @ViewChild('pdfContainer') pdfContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('trainingScreenshotInput') trainingScreenshotInput?: ElementRef<HTMLInputElement>;
  @ViewChild('trainingComposerInput') trainingComposerInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('trainingClarificationTextarea') trainingClarificationInputElement?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('trainingTimeline') trainingTimeline?: ElementRef<HTMLDivElement>;

  // Bound wheel handler for cleanup
  private boundWheelHandler = this.onPdfWheel.bind(this);
  
  // Bound click handler for dropdown dismiss
  private boundClickOutsideHandler = this.onClickOutside.bind(this);

  // Bound keydown handler for snip cancel
  private boundSnipEscapeHandler = this.onSnipEscapeKeydown.bind(this);

  documentModel: string = '';
  isAuthenticated: boolean = false;

  // File management properties
  files: File[] = [];
  selectedFile: File | null = null;
  isUploadingFile: boolean = false;
  loadingEvalFiles: boolean = false;
  evaluationFiles: EvaluationFile[] = [];

  // PDF SAS retry tracking
  private pdfSasRetryCount = 0;
  private pdfLoadingFileId = '';
  private readonly PDF_MAX_RETRIES = 3;

  // Prompt tuning properties
  fieldNames: string[] = [];
  selectedFieldName: string = '';
  fieldNameInput: string = ''; // Input text for fuzzy search
  filteredFieldNames: string[] = []; // Filtered list based on input
  showFieldNameDropdown: boolean = false; // Show/hide dropdown
  loadingFieldNames: boolean = false; // Loading state for field names
  currentPrompt: string = '';
  revisedPrompt: string = '';
  
  // Field schema storage
  fieldSchemaMap: Map<string, any> = new Map();
  
  // Version history
  versionHistory: any[] = [];
  showVersionsDropdown: boolean = false;
  loadingVersions: boolean = false;
  
  // Undo baseline
  originalRevisedPrompt: string = '';
  
  // Edit mode tracking
  isEditingCurrentPrompt: boolean = false;
  isEditingRevisedPrompt: boolean = false;
  tempCurrentPrompt: string = '';
  tempRevisedPrompt: string = '';

  // PDF Viewer properties
  pdfSrc: string = '';
  isLoadingPdf: boolean = false;
  loadError: string = '';
  currentPage: number = 1;
  pdfReady: boolean = false;
  totalPages: number = 0;
  zoom: number = 1.0;
  zoomPercentage: number = 100;
  pageInput: number | string = 1;
  rotation: number = 0;
  pageRotations: Map<number, number> = new Map(); // Track rotation per page (page number -> degrees)

  // File column collapse state
  isFileColumnCollapsed: boolean = false;

  // User-resizable desktop column widths
  private filesColumnWidthPx: number = 320;
  private tuningColumnWidthPx: number = 420;
  private readonly splitterWidthPx: number = 10;
  private readonly collapsedFilesColumnWidthPx: number = 40;
  private readonly minFilesColumnWidthPx: number = 180;
  private readonly minViewerColumnWidthPx: number = 360;
  private readonly minTuningColumnWidthPx: number = 300;
  private readonly desktopBreakpointPx: number = 992;
  private readonly defaultFilesRatio: number = 1 / 4.2;
  private readonly defaultTuningRatio: number = 1 / 4.2;
  private hasUserCustomizedColumnWidths: boolean = false;
  private promptTabTuningWidthSnapshotPx: number | null = null;

  private activeColumnSplitter: 'files-viewer' | 'viewer-tuning' | null = null;
  private resizeStartX: number = 0;
  private resizeStartFilesWidthPx: number = 0;
  private resizeStartTuningWidthPx: number = 0;
  private boundColumnResizeMoveHandler = (event: PointerEvent) => this.onColumnResizePointerMove(event);
  private boundColumnResizeUpHandler = (event: PointerEvent) => this.onColumnResizePointerUp(event);
  isColumnResizeDragging: boolean = false;
  isColumnLayoutReady: boolean = false;
  private pendingColumnResizeFrameId: number | null = null;

  // Deep-link pending selections from Validation Results
  private pendingFieldFromQuery: string | null = null;
  private pendingFileIdFromQuery: string | null = null;
  private pendingFileNameFromQuery: string | null = null;
  private pendingSelectionHandled: boolean = false;

  // Return navigation context when arriving from Validation Results via T action
  showBackToValidationFromT: boolean = false;
  private returnLoanId: string | null = null;
  private returnRunId: string | null = null;
  private returnDocumentCode: string | null = null;
  private returnTab: string = 'details';
  private returnEvaluationId: string | null = null;
  private promptTuningEntrySource: string = '';

  // Right-panel tabs and training chat state
  activeTuningTab: 'prompt' | 'training' = 'prompt';
  trainingMode: 'chat' | 'description' = 'chat';
  // Context text used as system description source for training ai-suggest.
  trainingDescriptionInput: string = '';
  isTrainingContextModalOpen: boolean = false;
  trainingContextDraftInput: string = '';
  trainingMessageInput: string = '';
  trainingMessages: TrainingChatMessage[] = [];
  isTrainingSending: boolean = false;
  trainingContextAttachment: {
    type: 'screenshot' | 'preview';
    label: string;
    fileName?: string;
    previewUrl?: string;
    imageBlob?: Blob;
  } | null = null;
  private trainingMessageIdCounter: number = 0;
  private trainingTypewriterTimers = new Map<number, any>();
  trainingApiMessages: AiSuggestMessage[] = [];
  trainingSuggestStatus: 'idle' | 'needs_clarification' | 'ready' | 'cancelled' | 'error' = 'idle';
  trainingCanGenerate: boolean = false;
  isTrainingGeneratingDirectly: boolean = false;
  private trainingAiSuggestSubscription: Subscription | null = null;
  isTrainingResponseModalOpen: boolean = false;
  trainingSelectedReadyDetails: TrainingReadyDetailsView | null = null;
  trainingSelectedReadyDetailsList: TrainingReadyDetailsView[] = [];
  trainingOriginalReadyDetailsList: TrainingReadyDetailsView[] = [];
  isTrainingFieldPopupOpen: boolean = false;
  trainingFieldPopupRowIndex: number = -1;
  trainingFieldPopupColumn: TrainingEditableDetailColumn | null = null;
  trainingFieldPopupInitialValue: string = '';
  trainingFieldPopupDraftValue: string = '';
  trainingFieldPopupError: string = '';
  isTrainingFieldPopupJsonUploading: boolean = false;
  private trainingFieldPopupJsonFileSelected: boolean = false;
  private trainingFieldPopupUploadedFieldSchemaValue: string | null = null;
  private trainingFieldPopupUploadedComputeSchemaValue: string | null = null;
  private trainingPendingLockedDetailsForContinue: TrainingReadyDetailsView[] | null = null;
  trainingClarificationQuestions: string[] = [];
  trainingClarificationAnswers: string[] = [];
  trainingClarificationIndex: number = -1;
  trainingClarificationInput: string = '';

  isTrainingScreenshotModalOpen: boolean = false;
  trainingScreenshotModalUrl: string = '';
  trainingScreenshotModalTitle: string = '';

  isTrainingSnipActive: boolean = false;
  isTrainingSnipDragging: boolean = false;
  isTrainingSnipCapturing: boolean = false;
  trainingSnipSelection: TrainingSnipSelection | null = null;
  private trainingSnipStartX: number = 0;
  private trainingSnipStartY: number = 0;
  private readonly trainingGenerateDraftStorageKey = 'promptTuningGenerateDraft';

  trainingFieldTypeOptions: string[] = ['Normal', 'Array', 'Complex', 'Compute'];
  private readonly trainingSimpleFeatureOptions: string[] = ['Normal', 'Seal', 'Signature', 'Date'];
  private readonly trainingArrayFeatureOptions: string[] = ['Normal', 'Signature', 'Date'];

  get isTrainingResponseContinueDisabled(): boolean {
    return this.isTrainingSending || !this.hasTrainingResponseDetailsChanged() || this.hasTrainingResponseFieldNameDuplicates;
  }

  get hasTrainingResponseFieldNameDuplicates(): boolean {
    return this.trainingSelectedReadyDetailsList.some((_, index) => this.isTrainingModalFieldNameDuplicate(index));
  }

  get hasTrainingResponseExistingFieldNameConflicts(): boolean {
    return this.trainingSelectedReadyDetailsList.some((_, index) => this.isTrainingModalFieldNameAlreadyExists(index));
  }

  get hasTrainingResponseFieldNameConflicts(): boolean {
    return this.hasTrainingResponseFieldNameDuplicates || this.hasTrainingResponseExistingFieldNameConflicts;
  }

  get hasTrainingResponseMissingRequiredFields(): boolean {
    return this.trainingSelectedReadyDetailsList.some(detail => (
      this.isTrainingModalRequiredFieldEmpty(detail, 'fieldName') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'fieldType') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'feature') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'prompt') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'additionalPrompt') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'fieldSchema') ||
      this.isTrainingModalRequiredFieldEmpty(detail, 'computeSchema')
    ));
  }

  get isTrainingGenerateActionDisabled(): boolean {
    return this.isTrainingGeneratingDirectly || this.hasTrainingResponseFieldNameConflicts || this.hasTrainingResponseMissingRequiredFields;
  }

  get hasTrainingContextValue(): boolean {
    return !!this.trainingDescriptionInput.trim();
  }

  get isTrainingFieldPopupSchemaColumn(): boolean {
    return this.trainingFieldPopupColumn === 'fieldSchema' || this.trainingFieldPopupColumn === 'computeSchema';
  }

  get isTrainingFieldPopupDoneDisabled(): boolean {
    if (!this.isTrainingFieldPopupOpen) {
      return true;
    }

    if (this.trainingFieldPopupError) {
      return true;
    }

    return this.trainingFieldPopupDraftValue === this.trainingFieldPopupInitialValue;
  }

  get trainingFieldPopupColumnLabel(): string {
    switch (this.trainingFieldPopupColumn) {
      case 'prompt':
        return 'Prompt';
      case 'additionalPrompt':
        return 'Additional Prompt';
      case 'fieldSchema':
        return 'Field Schema';
      case 'computeSchema':
        return 'Compute Schema';
      default:
        return '';
    }
  }

  get trainingFieldPopupFieldName(): string {
    if (this.trainingFieldPopupRowIndex < 0) {
      return '';
    }

    const detail = this.trainingSelectedReadyDetailsList[this.trainingFieldPopupRowIndex];
    return detail ? this.normalizeTrainingDetailValue(detail.fieldName) || 'Unnamed field' : '';
  }

  get shouldShowTrainingFieldSchemaUploadButton(): boolean {
    if (!this.isTrainingFieldPopupOpen || this.trainingFieldPopupColumn !== 'fieldSchema') {
      return false;
    }

    const detail = this.trainingSelectedReadyDetailsList[this.trainingFieldPopupRowIndex];
    const fieldType = String(detail?.fieldType || '').trim().toLowerCase();
    return fieldType === 'complex';
  }

  get shouldShowTrainingComputeSchemaUploadButton(): boolean {
    if (!this.isTrainingFieldPopupOpen || this.trainingFieldPopupColumn !== 'computeSchema') {
      return false;
    }

    const detail = this.trainingSelectedReadyDetailsList[this.trainingFieldPopupRowIndex];
    const fieldType = String(detail?.fieldType || '').trim().toLowerCase();
    return fieldType === 'complex' || fieldType === 'compute';
  }

  get isTrainingStopActionVisible(): boolean {
    return this.isTrainingSending || this.isTrainingClarificationActive();
  }

  get isTrainingPrimaryActionDisabled(): boolean {
    if (this.isTrainingStopActionVisible) {
      return false;
    }

    return !this.selectedFile;
  }

  get promptTuningBackTooltip(): string {
    if (this.showBackToValidationFromT) {
      return 'Back to Validation Results';
    }
    return this.promptTuningEntrySource === 'admin-settings-prompt-tuning'
      ? 'Back to Admin'
      : 'Back to Evaluation';
  }

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private truthManagementService: TruthManagementService,
    private documentModelService: DocumentModelService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(queryParams => {
      this.pendingFieldFromQuery = queryParams['field'] || null;
      this.pendingFileIdFromQuery = queryParams['fileId'] || null;
      this.pendingFileNameFromQuery = queryParams['fileName'] || null;
      this.pendingSelectionHandled = false;

      const source = queryParams['source'] || null;
      this.promptTuningEntrySource = String(source || '').trim().toLowerCase();
      this.showBackToValidationFromT = source === 'validation-matrix-tuning';
      this.returnLoanId = queryParams['returnLoanId'] || null;
      this.returnRunId = queryParams['returnRunId'] || null;
      this.returnDocumentCode = queryParams['returnDocumentCode'] || null;
      this.returnTab = queryParams['returnTab'] || 'details';
      this.returnEvaluationId = queryParams['returnEvaluationId'] || null;

      if (!this.showBackToValidationFromT) {
        this.returnLoanId = null;
        this.returnRunId = null;
        this.returnDocumentCode = null;
        this.returnTab = 'details';
        this.returnEvaluationId = null;
      }

      this.tryApplyPendingSelections();
    });

    // Get document model from route parameters
    this.route.params.subscribe(params => {
      if (params['documentModel']) {
        this.documentModel = params['documentModel'];
        // Load files for this document model
        this.loadEvaluationFiles(this.documentModel);
        // Field schemas will load when a file is selected
      }
    });

    // Check authentication status
    this.isAuthenticated = this.authService.hasValidToken();
    
    // Initialize filtered field names (empty until file selected)
    this.filteredFieldNames = [];
  }

  ngAfterViewInit(): void {
    // Attach wheel zoom listener to PDF container
    this.setupPdfWheelZoom();
    
    // Add click listener for dropdown dismiss
    document.addEventListener('click', this.boundClickOutsideHandler);

    // Add escape listener for snip cancel
    document.addEventListener('keydown', this.boundSnipEscapeHandler);

    // Initialize desktop resize layout bounds after view is ready.
    setTimeout(() => {
      this.initializeDefaultColumnWidths();
      this.enforceColumnWidthBounds();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy(): void {
    // Clean up wheel listener
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.removeEventListener('wheel', this.boundWheelHandler);
    }
    
    // Clean up click listener
    document.removeEventListener('click', this.boundClickOutsideHandler);

    // Clean up snip escape listener
    document.removeEventListener('keydown', this.boundSnipEscapeHandler);

    // Clean up any active column resize listeners
    this.stopColumnResizeDrag();

    // Clean up typewriter timers
    this.trainingTypewriterTimers.forEach(timer => clearInterval(timer));
    this.trainingTypewriterTimers.clear();

    if (this.trainingAiSuggestSubscription) {
      this.trainingAiSuggestSubscription.unsubscribe();
      this.trainingAiSuggestSubscription = null;
    }
    
    
    // Invalidate field schema cache when navigating away
    if (this.documentModel) {
      this.truthManagementService.invalidateDocumentModelCache(this.documentModel);
    }
  }

  getUserRole(): string {
    return this.authService.currentRole;
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  logout(): void {
    this.authService.logout();
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  navigateBackToValidationFromT(): void {
    if (!this.showBackToValidationFromT || !this.returnLoanId || !this.returnRunId || !this.returnDocumentCode) {
      return;
    }

    const queryParams: Record<string, string> = {
      tab: this.returnTab || 'details'
    };

    if (this.returnEvaluationId && this.returnEvaluationId !== 'Not Available' && this.returnEvaluationId !== 'latest') {
      queryParams['evaluationId'] = this.returnEvaluationId;
    }

    this.router.navigate(['/run-validation', this.returnLoanId, this.returnRunId, this.returnDocumentCode], { queryParams });
  }

  navigateBackFromPromptTuning(): void {
    if (this.showBackToValidationFromT && this.returnLoanId && this.returnRunId && this.returnDocumentCode) {
      this.navigateBackToValidationFromT();
      return;
    }
    if (this.promptTuningEntrySource === 'admin-settings-prompt-tuning') {
      this.router.navigate(['/admin-settings']);
      return;
    }

    this.router.navigate(['/dashboard']);
  }

  // PDF Navigation Methods
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageInput = this.currentPage;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pageInput = this.currentPage;
    }
  }

  goToPage(): void {
    const pageNum = parseInt(String(this.pageInput), 10);
    
    if (isNaN(pageNum)) {
      this.pageInput = this.currentPage;
      return;
    }
    
    if (pageNum < 1) {
      this.currentPage = 1;
    } else if (pageNum > this.totalPages) {
      this.currentPage = this.totalPages;
    } else {
      this.currentPage = pageNum;
    }
    
    this.pageInput = this.currentPage;
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.goToPage();
      }
      return;
    }
    
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // PDF Zoom Methods
  zoomIn(): void {
    if (this.zoom < 3.0) {
      this.applyZoomWithCenterAnchor(this.zoom + 0.25);
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.applyZoomWithCenterAnchor(this.zoom - 0.25);
    }
  }

  setZoomByPercentage(): void {
    const zoomNum = parseInt(String(this.zoomPercentage), 10);
    
    if (isNaN(zoomNum)) {
      this.zoomPercentage = Math.round(this.zoom * 100);
      return;
    }
    
    if (zoomNum < 50) {
      this.zoomPercentage = 50;
    } else if (zoomNum > 300) {
      this.zoomPercentage = 300;
    } else {
      this.zoomPercentage = zoomNum;
    }
    
    this.applyZoomWithCenterAnchor(this.zoomPercentage / 100);
  }

  private applyZoomWithCenterAnchor(nextZoom: number): boolean {
    const viewer = this.getPdfViewerScrollContainer();
    const centerX = viewer ? viewer.clientWidth / 2 : 0;
    const centerY = viewer ? viewer.clientHeight / 2 : 0;
    return this.applyZoomWithAnchor(nextZoom, centerX, centerY);
  }

  private getPdfViewerScrollContainer(): HTMLElement | null {
    if (!this.pdfContainer?.nativeElement) {
      return null;
    }

    return this.pdfContainer.nativeElement.querySelector('.ng2-pdf-viewer-container') as HTMLElement | null;
  }

  private applyZoomWithAnchor(nextZoom: number, anchorOffsetX: number, anchorOffsetY: number): boolean {
    const clampedZoom = Math.min(3.0, Math.max(0.5, nextZoom));
    const oldZoom = this.zoom;
    if (Math.abs(clampedZoom - oldZoom) < 0.0001) {
      return false;
    }

    const viewer = this.getPdfViewerScrollContainer();
    const boundedOffsetX = viewer ? Math.min(Math.max(anchorOffsetX, 0), viewer.clientWidth) : 0;
    const boundedOffsetY = viewer ? Math.min(Math.max(anchorOffsetY, 0), viewer.clientHeight) : 0;
    const ratioX = viewer && viewer.scrollWidth > 0
      ? (viewer.scrollLeft + boundedOffsetX) / viewer.scrollWidth
      : 0;
    const ratioY = viewer && viewer.scrollHeight > 0
      ? (viewer.scrollTop + boundedOffsetY) / viewer.scrollHeight
      : 0;

    this.zoom = clampedZoom;
    this.zoomPercentage = Math.round(this.zoom * 100);
    this.cdr.detectChanges();

    this.restorePdfZoomAnchor(ratioX, ratioY, boundedOffsetX, boundedOffsetY);
    return true;
  }

  private restorePdfZoomAnchor(ratioX: number, ratioY: number, anchorOffsetX: number, anchorOffsetY: number): void {
    const restore = () => {
      const viewer = this.getPdfViewerScrollContainer();
      if (!viewer) {
        return;
      }

      const nextLeft = (ratioX * viewer.scrollWidth) - anchorOffsetX;
      const nextTop = (ratioY * viewer.scrollHeight) - anchorOffsetY;
      const maxScrollLeft = Math.max(0, viewer.scrollWidth - viewer.clientWidth);
      const maxScrollTop = Math.max(0, viewer.scrollHeight - viewer.clientHeight);

      viewer.scrollLeft = Math.max(0, Math.min(maxScrollLeft, nextLeft));
      viewer.scrollTop = Math.max(0, Math.min(maxScrollTop, nextTop));
    };

    requestAnimationFrame(restore);
    setTimeout(restore, 40);
    setTimeout(restore, 120);
  }

  onZoomInputKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.setZoomByPercentage();
      }
      return;
    }
    
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // PDF Rotation Method
  /**
   * Rotate current page by 90 degrees clockwise
   * Uses cumulative rotation to always rotate in same direction
   */
  rotatePdf(): void {
    if (!this.pdfSrc || !this.pdfReady) {
      return;
    }

    // Get current page's cumulative rotation (default to 0 if not set)
    const currentRotation = this.pageRotations.get(this.currentPage) || 0;
    
    // Always increment by 90° - never reset to 0°
    // This ensures CSS always animates clockwise: 0° → 90° → 180° → 270° → 360° → 450° ...
    const newRotation = currentRotation + 90;
    
    // Store cumulative rotation (don't use modulo)
    this.pageRotations.set(this.currentPage, newRotation);
    
    // Apply rotation with smooth animation
    this.applyPageRotation(this.currentPage, newRotation, true);
    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Apply CSS transform rotation to a specific page container
   * @param pageNumber - Page number (1-indexed)
   * @param rotation - Cumulative rotation in degrees (0, 90, 180, 270, 360, 450, ...)
   * @param withTransition - Whether to animate the rotation (default: true)
   */
  private applyPageRotation(pageNumber: number, rotation: number, withTransition: boolean = true): void {
    if (!this.pdfContainer?.nativeElement) {
      return;
    }

    // Find the page container in DOM
    // ng2-pdf-viewer renders pages with class 'page' and data-page-number attribute
    const viewer = this.pdfContainer.nativeElement;
    const pageContainer = viewer.querySelector(`[data-page-number="${pageNumber}"]`) as HTMLElement;
    
    if (!pageContainer) {
      return;
    }

    // Calculate visual rotation (0-360 range) for aspect ratio calculations
    const visualRotation = rotation % 360;
    
    // Add or remove transition
    if (withTransition) {
      pageContainer.style.transition = 'transform 0.3s ease-in-out';
    } else {
      pageContainer.style.transition = 'none';
    }
    
    // Get the canvas element inside the page to determine dimensions
    const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement;
    
    let transformValue = `rotate(${rotation}deg)`;
    
    // For 90° and 270° visual rotations, apply scaling to fit within bounds
    // because the page dimensions are effectively swapped (width becomes height)
    if (canvas && (visualRotation === 90 || visualRotation === 270)) {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Calculate aspect ratio to determine how much to scale
      if (canvasWidth > 0 && canvasHeight > 0) {
        const aspectRatio = canvasWidth / canvasHeight;
        
        // Scale factor to make rotated page fit within original bounds
        const scaleFactor = aspectRatio !== 1 ? Math.min(1 / aspectRatio, aspectRatio) : 1;
        
        transformValue = `rotate(${rotation}deg) scale(${scaleFactor})`;
      }
    }
    
    // Apply transform
    pageContainer.style.transform = transformValue;
    pageContainer.style.transformOrigin = 'center center';
  }

  /**
   * Apply all stored page rotations to visible pages
   * Called after PDF loads or page changes to reapply rotations
   */
  private applyAllPageRotations(): void {
    if (!this.pdfReady || this.pageRotations.size === 0) {
      return;
    }

    // Apply rotation to each page that has a stored rotation
    // Use withTransition=false to apply instantly (no animation when reapplying)
    this.pageRotations.forEach((rotation, pageNumber) => {
      this.applyPageRotation(pageNumber, rotation, false);
    });
  }

  // PDF Lifecycle Methods
  onPdfLoad(pdf: any): void {
    this.totalPages = pdf.numPages;
    this.pdfReady = true;
    this.isLoadingPdf = false;
    this.loadError = '';
    this.currentPage = 1;
    this.pageInput = 1;
    this.cdr.detectChanges();
    
    // Ensure wheel zoom is set up after PDF loads
    this.setupPdfWheelZoom();
    
    // Reapply page rotations after PDF loads
    setTimeout(() => {
      this.applyAllPageRotations();
    }, 100);
  }

  onPdfError(error: any): void {
    if (this.pdfSasRetryCount < this.PDF_MAX_RETRIES) {
      this.pdfSasRetryCount++;
      this.refreshPdfFromFilesApi(this.pdfLoadingFileId);
    } else {
      this.pdfSrc = '';
      this.isLoadingPdf = false;
      this.pdfReady = false;
      this.loadError = 'Failed to load PDF after retries';
      this.cdr.detectChanges();
      this.notificationService.showError('Failed to load PDF after retries');
    }
  }

  onPageRendered(event: any): void {
    // Page rendered callback
    // Reapply rotations after page renders
    if (event?.pageNumber && this.pageRotations.has(event.pageNumber)) {
      const rotation = this.pageRotations.get(event.pageNumber);
      if (rotation !== undefined) {
        setTimeout(() => {
          this.applyPageRotation(event.pageNumber, rotation, false);
        }, 50);
      }
    }
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.pageInput = page;
  }

  onScroll(event: Event): void {
    // Handle scroll events if needed
  }

  // File Management Methods
  loadEvaluationFiles(documentType?: string): void {
    this.loadingEvalFiles = true;
    
    // Pass document_model param for server-side filter + Redis cache
    this.truthManagementService.getEvaluationFiles(documentType).subscribe({
      next: (allFiles: EvaluationFile[]) => {
        this.evaluationFiles = allFiles;
        
        // Convert to File format
        this.files = allFiles.map((ef, index) => {
          const uploadDate = ef.created_at
            ? new Date(ef.created_at.endsWith('Z') ? ef.created_at : ef.created_at + 'Z')
            : new Date();
          
          return {
            id: ef.uuid || String(index + 1),
            fileName: ef.file_name,
            filePath: ef.blob_sas_url || undefined,
            documentId: 0,
            uploadedDate: uploadDate
          } as File;
        });
        
        this.loadingEvalFiles = false;
        this.tryApplyPendingSelections();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingEvalFiles = false;
        this.files = [];
        this.cdr.detectChanges();
      }
    });
  }

  onFileSelectedAndUpload(event: any): void {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    // TODO: Implement file upload logic
    // For now, this is a placeholder
  }

  selectFileOnly(file: File): void {
    // Save current file's state before switching
    if (this.selectedFile) {
      this.saveCurrentFileState();
    }
    
    this.selectedFile = file;
    // Restore state for new file
    this.restoreFileState(file.id);
    
    // Load field schemas from API
    this.loadFieldSchemas();
    
    // Auto-collapse file column when file is selected
    this.isFileColumnCollapsed = true;
    this.applyTrainingAutoSplitOnFileSelection();
    
    // Clear page rotations when switching files - new PDF in standard format
    this.pageRotations.clear();
    
    // Load PDF for the selected file using the same API as truth-management
    this.loadPdfFromFile(file);
  }

  private applyTrainingAutoSplitOnFileSelection(): void {
    if (this.activeTuningTab !== 'training' || !this.isDesktopLayout()) {
      return;
    }

    this.enforceEqualSplitForCollapsedTrainingView();
    this.enforceColumnWidthBounds();
    this.dispatchPromptTuningResizeEventsDuringTransition();
  }

  private enforceEqualSplitForCollapsedTrainingView(): void {
    if (!this.isDesktopLayout() || this.activeTuningTab !== 'training' || !this.isFileColumnCollapsed) {
      return;
    }

    const contentWidth = this.getContentGridTrackSpaceWidth(4);
    if (contentWidth <= 0) {
      return;
    }

    const availableAfterSplitter = contentWidth - this.splitterWidthPx;
    if (availableAfterSplitter <= 0) {
      return;
    }

    // Exact 50/50 split: (collapsed file + preview) side equals training side.
    const targetTuningWidth = Math.round((availableAfterSplitter + this.getContentGridColumnGapPx()) / 2);
    const maxTuningWidth = Math.max(
      this.minTuningColumnWidthPx,
      contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.minViewerColumnWidthPx
    );
    this.tuningColumnWidthPx = this.clamp(targetTuningWidth, this.minTuningColumnWidthPx, maxTuningWidth);
  }

  loadPdfFromFile(file: File): void {
    if (!file.id) {
      this.pdfSrc = '';
      this.isLoadingPdf = false;
      return;
    }
    this.isLoadingPdf = true;
    this.loadError = '';
    this.pdfReady = false;
    this.tryStreamPdf(file.id);
  }

  private tryStreamPdf(fileId: string | number): void {
    const fileIdStr = fileId.toString();
    this.pdfLoadingFileId = fileIdStr;
    this.pdfSasRetryCount = 0;
    this.pdfReady = false;

    const sasUrl = this.getEvalFileSasUrl(fileIdStr);
    if (sasUrl) {
      this.pdfSrc = sasUrl;
      this.isLoadingPdf = false;
      this.zoom = 1.0;
      this.cdr.detectChanges();
    } else {
      this.refreshPdfFromFilesApi(fileIdStr);
    }
  }

  private getEvalFileSasUrl(fileIdStr: string): string | null {
    const evalFile = this.evaluationFiles.find(ef => ef.uuid === fileIdStr);
    return evalFile?.blob_sas_url || null;
  }

  private refreshPdfFromFilesApi(fileIdStr: string): void {
    this.truthManagementService.getEvaluationFiles(this.documentModel).subscribe({
      next: (files) => {
        this.evaluationFiles = files;
        const evalFile = files.find(f => f.uuid === fileIdStr);
        const sasUrl = evalFile?.blob_sas_url;
        if (sasUrl) {
          this.pdfSrc = sasUrl;
          this.isLoadingPdf = false;
          this.loadError = '';
          this.zoom = 1.0;
          this.cdr.detectChanges();
        } else {
          this.pdfSrc = '';
          this.isLoadingPdf = false;
          this.pdfReady = false;
          this.loadError = 'PDF not available for this file';
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.pdfSrc = '';
        this.isLoadingPdf = false;
        this.pdfReady = false;
        this.loadError = 'Failed to load PDF';
        this.cdr.detectChanges();
      }
    });
  }

  getFileStatus(fileId: number | string): any {
    // TODO: Implement file status logic
    return null;
  }

  getFileStatusClass(fileId: number | string): string {
    // TODO: Implement status class logic
    return '';
  }

  deleteFile(event: Event, file: any): void {
    event.stopPropagation();
    // TODO: Implement file deletion logic
  }

  // Prompt editing methods
  startEditingCurrentPrompt(): void {
    this.tempCurrentPrompt = this.currentPrompt;
    this.isEditingCurrentPrompt = true;
    this.cdr.detectChanges();
  }

  startEditingRevisedPrompt(): void {
    this.tempRevisedPrompt = this.revisedPrompt;
    this.isEditingRevisedPrompt = true;
    this.cdr.detectChanges();
  }

  saveCurrentPrompt(): void {
    this.currentPrompt = this.tempCurrentPrompt;
    this.isEditingCurrentPrompt = false;
  }

  saveRevisedPrompt(): void {
    this.revisedPrompt = this.tempRevisedPrompt;
    this.isEditingRevisedPrompt = false;
  }

  cancelCurrentPromptEdit(): void {
    this.isEditingCurrentPrompt = false;
    this.tempCurrentPrompt = this.currentPrompt;
  }

  cancelRevisedPromptEdit(): void {
    this.isEditingRevisedPrompt = false;
    this.tempRevisedPrompt = this.revisedPrompt;
  }

  // Field Name Autocomplete Methods
  onFieldNameInput(): void {
    const input = this.fieldNameInput.toLowerCase().trim();
    
    if (!input) {
      // Show all options if input is empty
      this.filteredFieldNames = [...this.fieldNames];
    } else {
      // Fuzzy match - filter field names that contain the input text
      this.filteredFieldNames = this.fieldNames.filter(fieldName => 
        fieldName.toLowerCase().includes(input)
      );
    }
    
    this.showFieldNameDropdown = true;
  }

  onFieldNameFocus(): void {
    this.filteredFieldNames = [...this.fieldNames];
    this.showFieldNameDropdown = true;
  }

  /**
   * Extract description from field_schema, handling different field types
   * @param fieldSchema - The field_schema object
   * @returns The description string, or empty string if not found
   */
  private getFieldDescription(fieldSchema: any): string {
    if (!fieldSchema) return '';
    
    // Priority 1: items.description (for array fields)
    if (fieldSchema.items?.description) {
      return fieldSchema.items.description;
    }
    
    // Priority 2: root description (for boolean, string, and other simple types)
    if (fieldSchema.description) {
      return fieldSchema.description;
    }
    
    return '';
  }

  selectFieldName(fieldName: string): void {
    this.selectedFieldName = fieldName;
    this.fieldNameInput = fieldName;
    this.showFieldNameDropdown = false;
    
    // Get field schema from map and auto-populate current prompt
    const fieldSchema = this.fieldSchemaMap.get(fieldName);
    
    if (fieldSchema) {
      const description = this.getFieldDescription(fieldSchema.field_schema);
      if (description) {
        this.currentPrompt = description;
      } else {
        this.currentPrompt = '';
        this.notificationService.showError('Prompt not fetched for current prompt.');
      }
    } else {
      this.currentPrompt = '';
      this.notificationService.showError('Prompt not fetched for current prompt.');
    }
    
    // Clear revised prompt and baseline
    this.revisedPrompt = '';
    this.originalRevisedPrompt = '';
    
    this.cdr.detectChanges();
  }

  onFieldNameBlur(): void {
    // Delay to allow click event on dropdown items
    setTimeout(() => {
      this.showFieldNameDropdown = false;
      
      // If input doesn't match any field exactly, keep it as custom input
      if (!this.fieldNames.includes(this.fieldNameInput)) {
        this.selectedFieldName = this.fieldNameInput;
      }
      
      this.cdr.detectChanges();
    }, 150);
  }

  onFieldNameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.filteredFieldNames.length > 0) {
      // Select first filtered option on Enter
      this.selectFieldName(this.filteredFieldNames[0]);
      event.preventDefault();
    } else if (event.key === 'Escape') {
      this.showFieldNameDropdown = false;
    }
  }

  // Load field names (attributes) for the document model
  loadFieldNamesForDocument(documentModel: string): void {
    if (!documentModel) return;
    
    this.loadingFieldNames = true;
    
    this.truthManagementService.getDocumentKeys(documentModel).subscribe({
      next: (response) => {
        if (response.keys && response.keys.length > 0) {
          // Extract field names from keys (using key_label which is the display name)
          this.fieldNames = response.keys.map(key => key.key_label).filter(label => label);
          this.filteredFieldNames = [...this.fieldNames];
        } else {
          this.fieldNames = [];
          this.filteredFieldNames = [];
        }
        
        this.loadingFieldNames = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.fieldNames = [];
        this.filteredFieldNames = [];
        this.loadingFieldNames = false;
        this.cdr.detectChanges();
      }
    });
  }

  // Toggle file column collapse/expand
  toggleFileColumn(): void {
    this.isFileColumnCollapsed = !this.isFileColumnCollapsed;

    if (this.isFileColumnCollapsed) {
      this.stopColumnResizeDrag();
    } else {
      this.enforceColumnWidthBounds();
    }

    this.dispatchPromptTuningResizeEventsDuringTransition();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isDesktopLayout()) {
      this.stopColumnResizeDrag();
      return;
    }

    this.enforceColumnWidthBounds();
  }

  isAnyColumnResizeEnabled(): boolean {
    return this.isDesktopLayout();
  }

  canResizeSplitter(splitter: 'files-viewer' | 'viewer-tuning'): boolean {
    if (!this.isDesktopLayout()) {
      return false;
    }

    if (splitter === 'files-viewer') {
      return !this.isFileColumnCollapsed;
    }

    return true;
  }

  getContentGridStyles(): { [key: string]: string } {
    if (!this.isDesktopLayout()) {
      return {};
    }

    if (this.isFileColumnCollapsed) {
      return {
        'grid-template-columns': `${this.collapsedFilesColumnWidthPx}px minmax(${this.minViewerColumnWidthPx}px, 1fr) ${this.splitterWidthPx}px ${this.tuningColumnWidthPx}px`
      };
    }

    return {
      'grid-template-columns': `${this.filesColumnWidthPx}px ${this.splitterWidthPx}px minmax(${this.minViewerColumnWidthPx}px, 1fr) ${this.splitterWidthPx}px ${this.tuningColumnWidthPx}px`
    };
  }

  startColumnResize(event: PointerEvent, splitter: 'files-viewer' | 'viewer-tuning'): void {
    if (!this.canResizeSplitter(splitter)) {
      return;
    }

    event.preventDefault();

    const gridWidth = this.getContentGridWidth();
    if (gridWidth <= 0) {
      return;
    }

    this.activeColumnSplitter = splitter;
    this.isColumnResizeDragging = true;
    this.hasUserCustomizedColumnWidths = true;
    this.resizeStartX = event.clientX;
    this.resizeStartFilesWidthPx = this.filesColumnWidthPx;
    this.resizeStartTuningWidthPx = this.tuningColumnWidthPx;

    window.addEventListener('pointermove', this.boundColumnResizeMoveHandler);
    window.addEventListener('pointerup', this.boundColumnResizeUpHandler);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }

  private onColumnResizePointerMove(event: PointerEvent): void {
    if (!this.activeColumnSplitter || !this.canResizeSplitter(this.activeColumnSplitter)) {
      return;
    }

    const deltaX = event.clientX - this.resizeStartX;
    const trackCount = this.isFileColumnCollapsed ? 4 : 5;
    const contentWidth = this.getContentGridTrackSpaceWidth(trackCount);
    if (contentWidth <= 0) {
      return;
    }

    const splitterTotal = this.splitterWidthPx * 2;

    if (this.activeColumnSplitter === 'files-viewer') {
      const maxFilesWidth = Math.max(
        this.minFilesColumnWidthPx,
        contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.tuningColumnWidthPx
      );
      const nextFilesWidth = this.resizeStartFilesWidthPx + deltaX;
      this.filesColumnWidthPx = this.clamp(nextFilesWidth, this.minFilesColumnWidthPx, maxFilesWidth);
    } else {
      const maxTuningWidth = this.isFileColumnCollapsed
        ? Math.max(
          this.minTuningColumnWidthPx,
          contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.minViewerColumnWidthPx
        )
        : Math.max(
          this.minTuningColumnWidthPx,
          contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.filesColumnWidthPx
        );
      const nextTuningWidth = this.resizeStartTuningWidthPx - deltaX;
      this.tuningColumnWidthPx = this.clamp(nextTuningWidth, this.minTuningColumnWidthPx, maxTuningWidth);
    }

    this.queueColumnResizeRender();
  }

  private onColumnResizePointerUp(_event: PointerEvent): void {
    this.stopColumnResizeDrag();
    this.dispatchPromptTuningResizeEvent();
    this.cdr.detectChanges();
  }

  private stopColumnResizeDrag(): void {
    this.activeColumnSplitter = null;
    this.isColumnResizeDragging = false;
    if (this.pendingColumnResizeFrameId !== null) {
      cancelAnimationFrame(this.pendingColumnResizeFrameId);
      this.pendingColumnResizeFrameId = null;
    }
    window.removeEventListener('pointermove', this.boundColumnResizeMoveHandler);
    window.removeEventListener('pointerup', this.boundColumnResizeUpHandler);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }

  private queueColumnResizeRender(): void {
    if (this.pendingColumnResizeFrameId !== null) {
      return;
    }

    this.pendingColumnResizeFrameId = requestAnimationFrame(() => {
      this.pendingColumnResizeFrameId = null;
      this.cdr.detectChanges();
    });
  }

  private enforceColumnWidthBounds(): void {
    if (!this.isDesktopLayout()) {
      return;
    }

    const trackCount = this.isFileColumnCollapsed ? 4 : 5;
    const contentWidth = this.getContentGridTrackSpaceWidth(trackCount);
    if (contentWidth <= 0) {
      return;
    }

    if (this.isFileColumnCollapsed) {
      const maxTuningWidth = Math.max(
        this.minTuningColumnWidthPx,
        contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.minViewerColumnWidthPx
      );
      this.tuningColumnWidthPx = this.clamp(this.tuningColumnWidthPx, this.minTuningColumnWidthPx, maxTuningWidth);
      return;
    }

    const splitterTotal = this.splitterWidthPx * 2;
    const totalMinRequired = this.minFilesColumnWidthPx + this.minViewerColumnWidthPx + this.minTuningColumnWidthPx + splitterTotal;
    if (contentWidth <= totalMinRequired) {
      this.filesColumnWidthPx = this.minFilesColumnWidthPx;
      this.tuningColumnWidthPx = this.minTuningColumnWidthPx;
      return;
    }

    const maxFilesWidth = contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.minTuningColumnWidthPx;
    this.filesColumnWidthPx = this.clamp(this.filesColumnWidthPx, this.minFilesColumnWidthPx, maxFilesWidth);

    const maxTuningWidth = contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.filesColumnWidthPx;
    this.tuningColumnWidthPx = this.clamp(this.tuningColumnWidthPx, this.minTuningColumnWidthPx, maxTuningWidth);
  }

  private initializeDefaultColumnWidths(): void {
    if (!this.isDesktopLayout() || this.hasUserCustomizedColumnWidths) {
      return;
    }

    const contentWidth = this.getContentGridTrackSpaceWidth(5);
    if (contentWidth <= 0) {
      return;
    }

    const availableWidth = Math.max(0, contentWidth - (this.splitterWidthPx * 2));
    this.filesColumnWidthPx = Math.round(availableWidth * this.defaultFilesRatio);
    this.tuningColumnWidthPx = Math.round(availableWidth * this.defaultTuningRatio);
  }

  private getContentGridTrackSpaceWidth(trackCount: number): number {
    const gridWidth = this.getContentGridWidth();
    if (gridWidth <= 0) {
      return 0;
    }

    const gapTotal = this.getContentGridGapTotalPx(trackCount);
    return Math.max(0, gridWidth - gapTotal);
  }

  private getContentGridGapTotalPx(trackCount: number): number {
    const gaps = Math.max(0, trackCount - 1);
    return this.getContentGridColumnGapPx() * gaps;
  }

  private getContentGridColumnGapPx(): number {
    const gridElement = this.contentGrid?.nativeElement;
    if (!gridElement) {
      return 8;
    }

    const computed = window.getComputedStyle(gridElement);
    const gapValue = parseFloat(computed.columnGap || computed.gap || '8');
    return Number.isFinite(gapValue) ? gapValue : 8;
  }

  private getContentGridWidth(): number {
    const elementWidth = this.contentGrid?.nativeElement?.clientWidth || 0;
    if (elementWidth > 0) {
      return elementWidth;
    }

    return Math.max(0, window.innerWidth - 32);
  }

  private isDesktopLayout(): boolean {
    return window.innerWidth > this.desktopBreakpointPx;
  }

  private dispatchPromptTuningResizeEvent(): void {
    window.dispatchEvent(new Event('resize'));
  }

  private dispatchPromptTuningResizeEventsDuringTransition(): void {
    const resizeEvent = new Event('resize');
    const transitionDuration = 400;
    const intervals = 8;
    const intervalTime = transitionDuration / intervals;

    for (let i = 1; i <= intervals; i++) {
      setTimeout(() => {
        window.dispatchEvent(resizeEvent);
      }, i * intervalTime);
    }

    setTimeout(() => {
      window.dispatchEvent(resizeEvent);
    }, transitionDuration + 50);
  }

  // PDF Wheel Zoom Setup and Handler
  private setupPdfWheelZoom(): void {
    if (this.pdfContainer?.nativeElement) {
      // Remove existing listener to prevent duplicates
      this.pdfContainer.nativeElement.removeEventListener('wheel', this.boundWheelHandler);
      // Add wheel zoom listener
      this.pdfContainer.nativeElement.addEventListener('wheel', this.boundWheelHandler, { passive: false });
    }
  }

  private onPdfWheel(event: WheelEvent): void {
    // Only zoom when Ctrl key is held (standard zoom behavior)
    if (!event.ctrlKey) return;
    
    // Prevent browser zoom
    event.preventDefault();
    
    if (!this.pdfSrc) return;
    
    const container = this.pdfContainer.nativeElement;
    const viewer = container.querySelector('.ng2-pdf-viewer-container') as HTMLElement;
    if (!viewer) return;
    
    // Use viewer-relative cursor coordinates so zoom anchors to the visible PDF viewport.
    const viewerRect = viewer.getBoundingClientRect();
    const cursorX = Math.min(Math.max(event.clientX - viewerRect.left, 0), viewer.clientWidth);
    const cursorY = Math.min(Math.max(event.clientY - viewerRect.top, 0), viewer.clientHeight);
    
    // Store old zoom
    const oldZoom = this.zoom;
    
    // Calculate zoom delta - smooth increments (5% per scroll tick)
    const zoomDelta = event.deltaY > 0 ? -0.05 : 0.05;
    const newZoom = Math.min(3.0, Math.max(0.5, this.zoom + zoomDelta));
    
    if (newZoom === oldZoom) return;
    
    this.applyZoomWithAnchor(newZoom, cursorX, cursorY);
  }

  /**
   * Check if there are any unsaved prompt changes (manual edits only)
   */
  hasPromptChanges(): boolean {
    return !!(
      this.selectedFieldName && 
      this.revisedPrompt && 
      this.revisedPrompt.trim() !== '' &&
      this.revisedPrompt !== this.originalRevisedPrompt  // Check actual edits
    );
  }

  /**
   * Save prompt changes with change reason (simplified flow)
   */
  savePromptChanges(): void {
    if (!this.hasPromptChanges()) {
      return;
    }
    
    // Show reason input dialog directly (no confirmation)
    this.modalService.showPrompt(
      'Please enter the reason for this change (optional):',
      (changeReason: string) => {
        // Allow empty reason - user can save with null/empty if they want
        this.performSave(changeReason.trim());
      },
      {
        title: 'Change Reason',
        placeholder: 'Describe what changed and why (optional)...',
        confirmText: 'Save',
        cancelText: 'Cancel'
      }
    );
  }

  /**
   * Perform the actual save operation
   */
  private performSave(changeReason: string): void {
    // Get the current field schema to preserve structure
    const fieldSchema = this.fieldSchemaMap.get(this.selectedFieldName);
    if (!fieldSchema) {
      this.notificationService.showError('Field schema not found. Please try again.');
      return;
    }
    
    // Deep clone the entire field_schema object to preserve all properties
    const updatedFieldSchema: any = JSON.parse(JSON.stringify(fieldSchema.field_schema));
    
    // Update only the description (either in items.description or at the top level)
    if (updatedFieldSchema.items && typeof updatedFieldSchema.items === 'object') {
      // For array types with items, update items.description
      updatedFieldSchema.items.description = this.revisedPrompt;
    } else {
      // For other types, update top-level description
      updatedFieldSchema.description = this.revisedPrompt;
    }
    this.truthManagementService.updateFieldSchema(
      this.documentModel,
      this.selectedFieldName,
      changeReason,
      updatedFieldSchema
    ).subscribe({
      next: (response) => {
        this.notificationService.showPromptSaved(this.documentModel);
        
        // Update current prompt
        this.currentPrompt = this.revisedPrompt;
        
        // Clear revised prompt
        this.revisedPrompt = '';
        this.originalRevisedPrompt = '';
        
        // Clear saved state
        this.clearSavedState();
        
        // Reload field schemas to get updated data
        this.loadFieldSchemas();
        
        // Reload version history if dropdown is open
        if (this.showVersionsDropdown && this.selectedFieldName) {
          this.loadingVersions = true;
          this.truthManagementService.getFieldHistory(this.documentModel, this.selectedFieldName).subscribe({
            next: (historyResponse) => {
              const versions = historyResponse?.versions || [];
              this.versionHistory = Array.isArray(versions) ? versions.slice(0, 10) : [];
              this.loadingVersions = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              this.loadingVersions = false;
              this.cdr.detectChanges();
            }
          });
        }
      },
      error: (err) => {
        this.notificationService.showError('Prompt not saved');
      }
    });
  }

  /**
   * Load field schemas from API
   */
  private loadFieldSchemas(): void {
    if (!this.documentModel) return;
    
    this.loadingFieldNames = true;
    this.truthManagementService.getFieldSchemas(this.documentModel).subscribe({
      next: (response) => {
        // API returns: {status: 'SUCCESS', document_model: '...', field_count: N, fields: [...]}
        const fields = response?.fields || [];
        
        if (Array.isArray(fields) && fields.length > 0) {
          this.fieldNames = fields.map(f => f.field_name).filter(name => name);
          this.filteredFieldNames = [...this.fieldNames];
          this.fieldSchemaMap = new Map(fields.map(f => [f.field_name, f]));
        } else {
          this.fieldNames = [];
          this.filteredFieldNames = [];
        }
        this.loadingFieldNames = false;
        this.tryApplyPendingSelections();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.fieldNames = [];
        this.filteredFieldNames = [];
        this.loadingFieldNames = false;
        this.tryApplyPendingSelections();
        this.cdr.detectChanges();
      }
    });
  }

  private normalizeFileName(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split(/[\/\\]/);
    return (parts[parts.length - 1] || fileName).trim().toLowerCase();
  }

  private resolvePendingTargetFile(): File | null {
    if (!this.files.length) {
      return null;
    }

    if (this.pendingFileIdFromQuery) {
      const byId = this.files.find(file => String(file.id) === String(this.pendingFileIdFromQuery));
      if (byId) {
        return byId;
      }
    }

    if (this.pendingFileNameFromQuery) {
      const targetName = this.normalizeFileName(this.pendingFileNameFromQuery);
      const byName = this.files.find(file => this.normalizeFileName(file.fileName) === targetName);
      if (byName) {
        return byName;
      }
    }

    return null;
  }

  private resolvePendingFieldName(): string | null {
    if (!this.pendingFieldFromQuery || this.fieldNames.length === 0) {
      return null;
    }

    const raw = this.pendingFieldFromQuery.trim();

    const normalizeToken = (value: string): string => {
      return value
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '_')
        .replace(/^_+|_+$/g, '');
    };

    const candidates = new Set<string>();
    candidates.add(raw);
    candidates.add(raw.replace(/\s+/g, '_'));
    candidates.add(raw.replace(/_/g, ' '));

    const hasSuffix = /_\d+$/.test(raw);
    if (hasSuffix) {
      const base = raw.replace(/_\d+$/, '');
      candidates.add(base);
      candidates.add(base.replace(/_/g, ' '));
    } else {
      const withOne = `${raw}_1`;
      candidates.add(withOne);
      candidates.add(withOne.replace(/\s+/g, '_'));
      candidates.add(withOne.replace(/_/g, ' '));
    }

    for (const candidate of candidates) {
      const exact = this.fieldNames.find(name => name === candidate);
      if (exact) {
        return exact;
      }
    }

    const normalizedCandidates = new Set(Array.from(candidates).map(candidate => normalizeToken(candidate)));
    const normalizedMatch = this.fieldNames.find(name => normalizedCandidates.has(normalizeToken(name)));
    return normalizedMatch || null;
  }

  private tryApplyPendingSelections(): void {
    if (this.pendingSelectionHandled) {
      return;
    }

    const hasPending = !!(this.pendingFieldFromQuery || this.pendingFileIdFromQuery || this.pendingFileNameFromQuery);
    if (!hasPending) {
      return;
    }

    const targetFile = this.resolvePendingTargetFile();
    if (targetFile && (!this.selectedFile || String(this.selectedFile.id) !== String(targetFile.id))) {
      this.selectFileOnly(targetFile);
      return;
    }

    // Field selection requires schema map to be loaded for the selected file.
    if (!this.selectedFile || this.loadingFieldNames || this.fieldNames.length === 0) {
      return;
    }

    const fieldToSelect = this.resolvePendingFieldName();
    if (!fieldToSelect) {
      this.pendingSelectionHandled = true;
      return;
    }

    this.selectFieldName(fieldToSelect);
    this.pendingSelectionHandled = true;
  }

  /**
   * Handle clicks outside the dropdown to dismiss it
   */
  private onClickOutside(event: MouseEvent): void {
    if (!this.showVersionsDropdown) return;
    
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.versions-dropdown-popup');
    const button = target.closest('.btn-view-versions');
    
    // Close dropdown if click is outside both dropdown and button
    if (!dropdown && !button) {
      this.showVersionsDropdown = false;
      this.cdr.detectChanges();
    }
  }
  
  /**
   * Toggle versions dropdown
   */
  toggleVersionsDropdown(event?: MouseEvent): void {
    // Stop event from immediately triggering onClickOutside
    if (event) {
      event.stopPropagation();
    }
    
    if (!this.selectedFieldName) return;
    
    if (!this.showVersionsDropdown) {
      this.loadingVersions = true;
      this.showVersionsDropdown = true;
      this.cdr.detectChanges(); // Force UI update
      
      this.truthManagementService.getFieldHistory(this.documentModel, this.selectedFieldName).subscribe({
        next: (response) => {
          // API returns: {status: 'SUCCESS', document_model: '...', field_name: '...', version_count: N, versions: [...]}
          const versions = response?.versions || [];
          
          if (Array.isArray(versions) && versions.length > 0) {
            this.versionHistory = versions.slice(0, 10);
          } else {
            this.versionHistory = [];
            this.notificationService.showError('Version history not available for this field.');
          }
          this.loadingVersions = false;
          this.cdr.detectChanges(); // Force UI update
        },
        error: (err) => {
          // 404 means field has no history yet (not an error)
          if (err.status === 404) {
            this.notificationService.showError('No version history available for this field yet.');
          } else {
            this.notificationService.showError('Failed to load version history. Please try again.');
          }
          
          this.versionHistory = [];
          this.loadingVersions = false;
          this.showVersionsDropdown = false;
          this.cdr.detectChanges(); // Force UI update
        }
      });
    } else {
      this.showVersionsDropdown = false;
    }
  }

  /**
   * Select a version from history
   */
  selectVersion(version: any): void {
    const description = this.getFieldDescription(version.field_schema);
    
    if (description) {
      this.revisedPrompt = description;
      // Don't update originalRevisedPrompt - keep current prompt as baseline
      // This makes Save Changes button appear when version is selected
      
      this.showVersionsDropdown = false;
    } else {
      this.notificationService.showError('Selected version has no prompt data.');
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Format date and time for version display (using user's local timezone)
   */
  formatDateTime(dateString: string): string {
    if (!dateString) return '';
    // Parse as UTC to ensure correct browser timezone conversion
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Check if revised prompt is modified
   */
  isRevisedPromptModified(): boolean {
    return this.revisedPrompt !== this.originalRevisedPrompt && this.revisedPrompt.trim() !== '';
  }

  /**
   * Undo revised prompt changes
   */
  undoRevisedPrompt(): void {
    this.revisedPrompt = this.originalRevisedPrompt;
  }

  /**
   * Auto-resize textarea as content grows
   */
  autoResizeTextarea(event: any): void {
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  /**
   * AI button placeholder (no functionality yet)
   */
  handleAIAction(): void {
  }

  switchTuningTab(tab: 'prompt' | 'training'): void {
    if (this.activeTuningTab === tab) {
      return;
    }

    const previousTab = this.activeTuningTab;

    if (previousTab === 'prompt' && tab === 'training' && this.selectedFile && this.isFileColumnCollapsed && this.isDesktopLayout()) {
      this.promptTabTuningWidthSnapshotPx = this.tuningColumnWidthPx;
    }

    this.activeTuningTab = tab;

    if (tab === 'training' && this.selectedFile && this.isFileColumnCollapsed) {
      this.applyTrainingAutoSplitOnFileSelection();
      return;
    }

    if (tab === 'prompt' && previousTab === 'training' && this.promptTabTuningWidthSnapshotPx !== null && this.isDesktopLayout()) {
      this.tuningColumnWidthPx = this.promptTabTuningWidthSnapshotPx;
      this.enforceColumnWidthBounds();
      this.dispatchPromptTuningResizeEventsDuringTransition();
    }
  }

  onTrainingModeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const value = target.value === 'description' ? 'description' : 'chat';
    this.trainingMode = value;
  }

  setTrainingMode(mode: 'chat' | 'description'): void {
    this.trainingMode = mode;
  }

  onTrainingInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  onTrainingClarificationInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  onTrainingComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.sendTrainingMessage();
  }

  onTrainingClarificationKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.submitTrainingClarificationAnswer();
  }

  onTrainingPrimaryAction(): void {
    if (this.isTrainingStopActionVisible) {
      this.stopTrainingFlow();
      return;
    }

    if (this.isTrainingPrimaryActionDisabled) {
      return;
    }

    this.sendTrainingMessage();
  }

  private stopTrainingFlow(): void {
    const hadActiveFlow = this.isTrainingSending || this.isTrainingClarificationActive();

    if (this.trainingAiSuggestSubscription) {
      this.trainingAiSuggestSubscription.unsubscribe();
      this.trainingAiSuggestSubscription = null;
    }

    if (!hadActiveFlow) {
      return;
    }

    this.isTrainingSending = false;
    this.clearTrainingClarificationQueue();
    this.clearTrainingContinueLockedState();
    this.trainingSuggestStatus = 'cancelled';
    this.trainingCanGenerate = false;
    this.cdr.detectChanges();
  }

  private resetTrainingInputHeight(): void {
    const textarea = this.trainingComposerInput?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
  }

  private resetTrainingClarificationInputHeight(): void {
    const textarea = this.trainingClarificationInputElement?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
  }

  triggerTrainingScreenshotInput(): void {
    this.trainingScreenshotInput?.nativeElement?.click();
  }

  startTrainingSnipCapture(): void {
    if (!this.selectedFile) {
      return;
    }

    if (this.isTrainingSnipCapturing) {
      return;
    }

    if (!this.pdfSrc || this.isLoadingPdf || this.loadError || !this.pdfReady) {
      this.notificationService.showError('Load a PDF in Preview before taking a screenshot snip.');
      return;
    }

    const canvas = this.getActivePdfCanvas();
    if (!canvas) {
      this.notificationService.showError('Unable to start snipping because the PDF page is not ready yet.');
      return;
    }

    this.isTrainingSnipActive = true;
    this.isTrainingSnipDragging = false;
    this.trainingSnipSelection = null;
    this.cdr.detectChanges();
  }

  cancelTrainingSnipCapture(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.isTrainingSnipActive = false;
    this.isTrainingSnipDragging = false;
    this.isTrainingSnipCapturing = false;
    this.trainingSnipSelection = null;
    this.cdr.detectChanges();
  }

  onTrainingSnipPointerDown(event: PointerEvent): void {
    if (!this.isTrainingSnipActive || this.isTrainingSnipCapturing) {
      return;
    }

    const overlay = event.currentTarget as HTMLElement;
    const rect = overlay.getBoundingClientRect();
    const localX = this.clamp(event.clientX - rect.left, 0, rect.width);
    const localY = this.clamp(event.clientY - rect.top, 0, rect.height);

    this.trainingSnipStartX = localX;
    this.trainingSnipStartY = localY;
    this.isTrainingSnipDragging = true;
    this.trainingSnipSelection = {
      left: localX,
      top: localY,
      width: 0,
      height: 0
    };

    overlay.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  onTrainingSnipPointerMove(event: PointerEvent): void {
    if (!this.isTrainingSnipActive || !this.isTrainingSnipDragging || this.isTrainingSnipCapturing) {
      return;
    }

    const overlay = event.currentTarget as HTMLElement;
    const rect = overlay.getBoundingClientRect();
    const localX = this.clamp(event.clientX - rect.left, 0, rect.width);
    const localY = this.clamp(event.clientY - rect.top, 0, rect.height);

    this.trainingSnipSelection = this.buildSnipSelection(
      this.trainingSnipStartX,
      this.trainingSnipStartY,
      localX,
      localY
    );

    event.preventDefault();
  }

  async onTrainingSnipPointerUp(event: PointerEvent): Promise<void> {
    if (!this.isTrainingSnipActive || !this.isTrainingSnipDragging || this.isTrainingSnipCapturing) {
      return;
    }

    const overlay = event.currentTarget as HTMLElement;
    if (overlay.hasPointerCapture(event.pointerId)) {
      overlay.releasePointerCapture(event.pointerId);
    }

    this.isTrainingSnipDragging = false;

    if (!this.trainingSnipSelection || this.trainingSnipSelection.width < 0.5 || this.trainingSnipSelection.height < 0.5) {
      this.trainingSnipSelection = null;
      this.notificationService.showError('Snip area is too small. Drag to select a larger region.');
      this.cdr.detectChanges();
      return;
    }

    await this.captureTrainingSnipSelection();
    event.preventDefault();
  }

  onTrainingSnipPointerLeave(event: PointerEvent): void {
    if (!this.isTrainingSnipActive || !this.isTrainingSnipDragging) {
      return;
    }

    const overlay = event.currentTarget as HTMLElement;
    if (overlay.hasPointerCapture(event.pointerId)) {
      return;
    }

    this.isTrainingSnipDragging = false;
  }

  onTrainingScreenshotSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const previewUrl = typeof reader.result === 'string' ? reader.result : undefined;
        const screenshotLabel = this.formatTrainingScreenshotLabel();
        this.trainingContextAttachment = {
          type: 'screenshot',
          fileName: file.name,
          label: screenshotLabel,
          previewUrl,
          imageBlob: file
        };
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    } else {
      this.useCurrentPreviewContext();
    }

    input.value = '';
  }

  private async captureTrainingSnipSelection(): Promise<void> {
    if (!this.trainingSnipSelection) {
      return;
    }

    this.isTrainingSnipCapturing = true;

    try {
      const canvas = this.getActivePdfCanvas();
      if (!canvas) {
        this.notificationService.showError('Unable to capture screenshot. PDF canvas is not available.');
        return;
      }

      const canvasRect = canvas.getBoundingClientRect();
      if (canvasRect.width <= 0 || canvasRect.height <= 0) {
        this.notificationService.showError('Unable to capture screenshot. PDF canvas has invalid dimensions.');
        return;
      }

      const overlayRect = this.pdfContainer.nativeElement.getBoundingClientRect();
      const selectionViewportRect = {
        left: overlayRect.left + this.trainingSnipSelection.left,
        top: overlayRect.top + this.trainingSnipSelection.top,
        right: overlayRect.left + this.trainingSnipSelection.left + this.trainingSnipSelection.width,
        bottom: overlayRect.top + this.trainingSnipSelection.top + this.trainingSnipSelection.height
      };

      const clippedRect = this.getIntersectionRect(selectionViewportRect, {
        left: canvasRect.left,
        top: canvasRect.top,
        right: canvasRect.right,
        bottom: canvasRect.bottom
      });

      if (!clippedRect) {
        this.notificationService.showError('Selected area is outside the PDF page. Please snip over the document area.');
        return;
      }

      const scaleX = canvas.width / canvasRect.width;
      const scaleY = canvas.height / canvasRect.height;

      const srcX = Math.floor((clippedRect.left - canvasRect.left) * scaleX);
      const srcY = Math.floor((clippedRect.top - canvasRect.top) * scaleY);
      const srcWidth = Math.floor((clippedRect.right - clippedRect.left) * scaleX);
      const srcHeight = Math.floor((clippedRect.bottom - clippedRect.top) * scaleY);

      if (srcWidth < 2 || srcHeight < 2) {
        this.notificationService.showError('Snip area is too small. Please select a larger area.');
        return;
      }

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = srcWidth;
      exportCanvas.height = srcHeight;

      const exportContext = exportCanvas.getContext('2d');
      if (!exportContext) {
        this.notificationService.showError('Unable to capture screenshot due to canvas context error.');
        return;
      }

      exportContext.drawImage(
        canvas,
        srcX,
        srcY,
        srcWidth,
        srcHeight,
        0,
        0,
        srcWidth,
        srcHeight
      );

      const imageBlob = await this.canvasToBlob(exportCanvas);
      const previewUrl = exportCanvas.toDataURL('image/png');

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `snip-${timestamp}.png`;
      const screenshotLabel = this.formatTrainingScreenshotLabel();
      this.trainingContextAttachment = {
        type: 'screenshot',
        fileName,
        label: screenshotLabel,
        previewUrl,
        imageBlob
      };

      this.isTrainingSnipActive = false;
      this.trainingSnipSelection = null;
      this.cdr.detectChanges();
    } catch (error) {
      this.notificationService.showError('Unable to capture screenshot. Please try again.');
    } finally {
      this.isTrainingSnipDragging = false;
      this.isTrainingSnipCapturing = false;
      this.cdr.detectChanges();
    }
  }

  private onSnipEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTrainingSnipActive) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelTrainingSnipCapture();
    }
  }

  private getActivePdfCanvas(): HTMLCanvasElement | null {
    const root = this.pdfContainer?.nativeElement;
    if (!root) {
      return null;
    }

    const currentPageCanvas = root.querySelector(
      `[data-page-number="${this.currentPage}"] canvas`
    ) as HTMLCanvasElement | null;

    if (currentPageCanvas) {
      return currentPageCanvas;
    }

    return root.querySelector('.page canvas') as HTMLCanvasElement | null;
  }

  private buildSnipSelection(startX: number, startY: number, endX: number, endY: number): TrainingSnipSelection {
    const left = Math.min(startX, endX);
    const top = Math.min(startY, endY);
    const width = Math.abs(endX - startX);
    const height = Math.abs(endY - startY);

    return { left, top, width, height };
  }

  private getIntersectionRect(
    first: { left: number; top: number; right: number; bottom: number },
    second: { left: number; top: number; right: number; bottom: number }
  ): { left: number; top: number; right: number; bottom: number } | null {
    const left = Math.max(first.left, second.left);
    const top = Math.max(first.top, second.top);
    const right = Math.min(first.right, second.right);
    const bottom = Math.min(first.bottom, second.bottom);

    if (right <= left || bottom <= top) {
      return null;
    }

    return { left, top, right, bottom };
  }

  private canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error('Canvas export returned an empty blob.'));
      }, 'image/png');
    });
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  removeTrainingContextAttachment(): void {
    this.closeTrainingScreenshotPreview();
    this.trainingContextAttachment = null;
  }

  openTrainingScreenshotPreview(): void {
    if (!this.trainingContextAttachment || this.trainingContextAttachment.type !== 'screenshot') {
      return;
    }

    if (!this.trainingContextAttachment.previewUrl) {
      this.notificationService.showError('Screenshot preview is not available for this attachment.');
      return;
    }

    this.trainingScreenshotModalUrl = this.trainingContextAttachment.previewUrl;
    this.trainingScreenshotModalTitle = this.trainingContextAttachment.fileName || 'Screenshot';
    this.isTrainingScreenshotModalOpen = true;
  }

  closeTrainingScreenshotPreview(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isTrainingScreenshotModalOpen = false;
    this.trainingScreenshotModalUrl = '';
    this.trainingScreenshotModalTitle = '';
  }

  useCurrentPreviewContext(): void {
    const fileName = this.selectedFile?.fileName || 'No file selected';
    const page = this.currentPage || 1;
    this.trainingContextAttachment = {
      type: 'preview',
      label: `Current page: ${fileName} - Page ${page}`
    };
  }

  getTrainingContextLineText(): string {
    if (this.selectedFile) {
      return `Current page will be used: ${this.selectedFile.fileName} - Page ${this.currentPage || 1}`;
    }

    return 'No screenshot selected. Current preview page will be used when available.';
  }

  shouldShowTrainingMessageContext(contextLabel: string): boolean {
    return String(contextLabel || '').trim().length > 0;
  }

  async sendTrainingMessage(): Promise<void> {
    const message = this.trainingMessageInput.trim();
    if (this.isTrainingSending || this.isTrainingClarificationActive()) {
      return;
    }

    // New user message starts a normal chat turn; clear any prior continue-lock intent.
    this.clearTrainingContinueLockedState();

    if (!this.trainingContextAttachment) {
      this.useCurrentPreviewContext();
    }

    const contextLabel = this.trainingContextAttachment?.label || 'Current page context';

    const effectiveMessage = message || this.buildTrainingFallbackMessage();
    this.appendTrainingMessage('user', effectiveMessage, contextLabel);

    this.ensureTrainingSystemMessage();
    this.trainingApiMessages.push({
      role: 'user',
      content: effectiveMessage
    });

    this.trainingMessageInput = '';
    this.resetTrainingInputHeight();
    await this.dispatchTrainingAiSuggestRequest(contextLabel);
  }

  private buildTrainingFallbackMessage(): string {
    if (this.trainingContextAttachment?.type === 'screenshot') {
      return 'Analyze the attached screenshot.';
    }

    const context = this.trainingDescriptionInput.trim();
    if (context) {
      return `Use context: ${context}`;
    }

    return 'Continue with current page context.';
  }

  isTrainingClarificationActive(): boolean {
    return this.trainingClarificationQuestions.length > 0 &&
      this.trainingClarificationIndex >= 0 &&
      this.trainingClarificationIndex < this.trainingClarificationQuestions.length;
  }

  getActiveTrainingClarificationQuestion(): string {
    if (!this.isTrainingClarificationActive()) {
      return '';
    }

    return this.trainingClarificationQuestions[this.trainingClarificationIndex] || '';
  }

  async submitTrainingClarificationAnswer(): Promise<void> {
    if (!this.isTrainingClarificationActive() || this.isTrainingSending) {
      return;
    }

    const answer = this.trainingClarificationInput.trim();
    if (!answer) {
      return;
    }

    const contextLabel = this.trainingContextAttachment?.label || 'Current page context';
    this.appendTrainingMessage('user', answer, contextLabel);
    this.trainingApiMessages.push({ role: 'user', content: answer });
    this.trainingClarificationAnswers.push(answer);
    this.trainingClarificationInput = '';
    this.resetTrainingClarificationInputHeight();

    const hasMoreQuestions = this.trainingClarificationIndex < this.trainingClarificationQuestions.length - 1;
    if (hasMoreQuestions) {
      this.trainingClarificationIndex += 1;
      const nextQuestion = this.getActiveTrainingClarificationQuestion();
      this.appendTrainingMessage(
        'assistant',
        nextQuestion,
        contextLabel,
        null,
        null,
        nextQuestion,
        'needs_clarification'
      );
      this.trainingSuggestStatus = 'needs_clarification';
      this.trainingCanGenerate = false;
      return;
    }

    this.clearTrainingClarificationQueue();
    await this.dispatchTrainingAiSuggestRequest(contextLabel);
  }

  private clearTrainingClarificationQueue(): void {
    this.trainingClarificationQuestions = [];
    this.trainingClarificationAnswers = [];
    this.trainingClarificationIndex = -1;
    this.trainingClarificationInput = '';
    this.resetTrainingClarificationInputHeight();
  }

  private async dispatchTrainingAiSuggestRequest(contextLabel: string): Promise<void> {
    this.isTrainingSending = true;
    this.trainingCanGenerate = false;
    this.trainingSuggestStatus = 'idle';
    this.cdr.detectChanges();

    if (this.trainingAiSuggestSubscription) {
      this.trainingAiSuggestSubscription.unsubscribe();
      this.trainingAiSuggestSubscription = null;
    }

    const imageBlob = await this.resolveTrainingContextImageBlob();
    const normalizedImage = await this.ensureTrainingImagePngOrJpeg(imageBlob);
    if (!normalizedImage) {
      this.isTrainingSending = false;
      this.trainingSuggestStatus = 'error';
      this.notificationService.showError('Image is required. Please attach a PNG or JPG screenshot.');
      this.cdr.detectChanges();
      return;
    }

    const imageFileName = this.resolveTrainingImageFileName(normalizedImage.type || 'image/png');

    // FormData file parts are shown as binary in browser network views; MIME remains image/png or image/jpeg.
    const requestSubscription = this.truthManagementService.suggestFieldWithAi(
      this.documentModel,
      this.trainingApiMessages,
      normalizedImage,
      imageFileName
    ).subscribe({
      next: (response: AiSuggestResponse) => {
        if (this.trainingAiSuggestSubscription !== requestSubscription) {
          return;
        }

        const assistantResult = this.normalizeAiSuggestResponse(response);

        const readyDetailsList = assistantResult.status === 'ready'
          ? this.resolveTrainingLockedReadyDetails(assistantResult.readyDetailsList)
          : assistantResult.readyDetailsList;
        const readyDetails = readyDetailsList.length > 0 ? readyDetailsList[0] : null;

        this.trainingApiMessages.push({
          role: 'assistant',
          content: assistantResult.content
        });

        if (assistantResult.status === 'needs_clarification' && assistantResult.questions.length > 0) {
          this.trainingClarificationQuestions = assistantResult.questions;
          this.trainingClarificationAnswers = [];
          this.trainingClarificationIndex = 0;
          this.trainingClarificationInput = '';
          this.resetTrainingClarificationInputHeight();

          const firstQuestion = this.trainingClarificationQuestions[0];
          this.appendTrainingMessage(
            'assistant',
            assistantResult.content,
            contextLabel,
            null,
            null,
            firstQuestion,
            'needs_clarification'
          );

          this.trainingSuggestStatus = 'needs_clarification';
          this.trainingCanGenerate = false;
          return;
        }

        this.clearTrainingClarificationQueue();

        this.appendTrainingMessage(
          'assistant',
          assistantResult.content,
          contextLabel,
          readyDetails,
          readyDetailsList,
          assistantResult.displayContent,
          assistantResult.status === 'ready'
            ? 'ready'
            : assistantResult.status === 'cancelled'
              ? 'cancelled'
              : null
        );

        if (assistantResult.status === 'ready') {
          this.clearTrainingContinueLockedState();
          this.trainingSuggestStatus = 'ready';
          this.trainingCanGenerate = true;
        } else if (assistantResult.status === 'cancelled') {
          this.clearTrainingContinueLockedState();
          this.trainingSuggestStatus = 'cancelled';
          this.trainingCanGenerate = false;
        } else {
          this.trainingSuggestStatus = 'error';
          this.trainingCanGenerate = false;
        }
      },
      error: (error) => {
        if (this.trainingAiSuggestSubscription !== requestSubscription) {
          return;
        }
        this.clearTrainingContinueLockedState();
        this.trainingSuggestStatus = 'error';
        this.trainingCanGenerate = false;
        this.isTrainingSending = false;
        this.trainingAiSuggestSubscription = null;
        this.notificationService.showError('Unable to get AI suggestion right now. Please try again.');
        this.cdr.detectChanges();
      },
      complete: () => {
        if (this.trainingAiSuggestSubscription === requestSubscription) {
          this.trainingAiSuggestSubscription = null;
        }
        this.isTrainingSending = false;
        this.cdr.detectChanges();
      }
    });

    this.trainingAiSuggestSubscription = requestSubscription;
  }

  private ensureTrainingSystemMessage(): void {
    if (this.trainingApiMessages.length > 0 && this.trainingApiMessages[0].role === 'system') {
      return;
    }

    this.trainingApiMessages.unshift({
      role: 'system',
      content: this.getTrainingSystemDescription()
    });
  }

  private getTrainingSystemDescription(): string {
    const typedDescription = this.trainingDescriptionInput?.trim();
    if (typedDescription) {
      return typedDescription;
    }

    const selectedPrompt = this.currentPrompt?.trim();
    if (selectedPrompt) {
      return selectedPrompt;
    }

    if (this.documentModel) {
      return `Document model context: ${this.documentModel}`;
    }

    return 'Document context is available in the attached page image.';
  }

  private async resolveTrainingContextImageBlob(): Promise<Blob | null> {
    if (this.trainingContextAttachment?.type === 'screenshot') {
      if (this.trainingContextAttachment.imageBlob) {
        return this.trainingContextAttachment.imageBlob;
      }

      const previewUrl = this.trainingContextAttachment.previewUrl;
      if (previewUrl) {
        try {
          return await this.dataUrlToBlob(previewUrl);
        } catch (error) {
        }
      }
    }

    const canvas = this.getActivePdfCanvas();
    if (!canvas) {
      return null;
    }

    try {
      return await this.canvasToBlob(canvas);
    } catch (error) {
      return null;
    }
  }

  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  private normalizeAiSuggestResponse(response: AiSuggestResponse): {
    content: string;
    displayContent: string;
    status: 'ready' | 'needs_clarification' | 'cancelled' | '';
    questions: string[];
    readyDetails: TrainingReadyDetailsView | null;
    readyDetailsList: TrainingReadyDetailsView[];
  } {
    const directContent = this.getRawAssistantResponseText(response);
    const parsedContent = this.tryParseAssistantContent(directContent);

    const status = this.normalizeAssistantStatus(
      parsedContent?.status ||
      response?.status ||
      this.extractStatusFromText(directContent)
    );

    const questions = this.normalizeAssistantQuestions(parsedContent?.questions || response?.questions);
    const readyDetailsList = status === 'ready'
      ? this.buildTrainingReadyDetailsList(parsedContent || response, directContent)
      : [];
    const readyDetails = readyDetailsList.length > 0 ? readyDetailsList[0] : null;
    const displayContent = this.getTrainingAssistantDisplayContent(directContent, questions, status, readyDetailsList.length);

    return { content: directContent, displayContent, status, questions, readyDetails, readyDetailsList };
  }

  private getRawAssistantResponseText(response: AiSuggestResponse): string {
    if (typeof response?.content === 'string' && response.content.trim().length > 0) {
      return response.content;
    }

    return JSON.stringify(response || {});
  }

  private tryParseAssistantContent(content: string): { status?: string; questions?: string[] } | null {
    if (!content) {
      return null;
    }

    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      const normalized = this.normalizeSingleQuoteJson(content);
      if (!normalized) {
        return null;
      }

      try {
        const parsed = JSON.parse(normalized);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch {
        return null;
      }
    }

    return null;
  }

  private normalizeSingleQuoteJson(content: string): string {
    const trimmed = String(content || '').trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return '';
    }

    return trimmed
      .replace(/([{,]\s*)'([^']+?)'\s*:/g, '$1"$2":')
      .replace(/:\s*'([^']*?)'(?=\s*[,}\]])/g, ': "$1"');
  }

  private extractStatusFromText(content: string): string {
    if (!content) {
      return '';
    }

    const match = content.match(/status\s*['"]?\s*:\s*['"]([a-z_]+)['"]/i);
    return match && match[1] ? match[1].toLowerCase() : '';
  }

  private normalizeAssistantStatus(status: unknown): 'ready' | 'needs_clarification' | 'cancelled' | '' {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'ready' || value === 'needs_clarification') {
      return value;
    }

    if (value === 'cancelled' || value === 'canceled') {
      return 'cancelled';
    }

    return '';
  }

  private normalizeAssistantQuestions(questions: unknown): string[] {
    if (!Array.isArray(questions)) {
      return [];
    }

    return questions
      .map(question => String(question || '').trim())
      .filter(question => question.length > 0);
  }

  private getTrainingAssistantDisplayContent(
    rawContent: string,
    questions: string[],
    status: 'ready' | 'needs_clarification' | 'cancelled' | '',
    readyCount: number = 0
  ): string {
    if (status === 'needs_clarification' && questions.length > 0) {
      return questions[0];
    }

    if (status === 'ready') {
      if (readyCount > 1) {
        return 'Ready. Click to view the newly generated fields.';
      }

      return 'Ready. Click to view the newly generated field.';
    }

    if (status === 'cancelled') {
      const parsed = this.tryParseAssistantContent(rawContent) as any;
      const reason = String(parsed?.message || parsed?.detail || parsed?.reason || '').trim();
      return reason ? `Cancelled: ${reason}` : 'Cancelled.';
    }

    return rawContent;
  }

  private appendTrainingMessage(
    role: 'user' | 'assistant',
    text: string,
    contextLabel: string,
    readyDetails: TrainingReadyDetailsView | null = null,
    readyDetailsList: TrainingReadyDetailsView[] | null = null,
    displayText?: string,
    statusType: 'ready' | 'needs_clarification' | 'cancelled' | null = null
  ): void {
    const message: TrainingChatMessage = {
      id: ++this.trainingMessageIdCounter,
      role,
      text,
      displayText: displayText ?? text,
      statusType,
      mode: this.trainingMode,
      contextLabel,
      createdAt: new Date(),
      renderedText: '',
      isTyping: false,
      readyDetails,
      readyDetailsList
    };

    this.trainingMessages.push(message);
    this.animateTrainingMessage(message);
    this.scrollTrainingTimelineToBottom();
  }

  private scrollTrainingTimelineToBottom(): void {
    const timeline = this.trainingTimeline?.nativeElement;
    if (!timeline) {
      return;
    }

    requestAnimationFrame(() => {
      timeline.scrollTop = timeline.scrollHeight;
    });
  }

  private animateTrainingMessage(message: TrainingChatMessage): void {
    const fullText = String(message.displayText || message.text || '');
    if (!fullText) {
      message.renderedText = '';
      message.isTyping = false;
      return;
    }

    const existingTimer = this.trainingTypewriterTimers.get(message.id);
    if (existingTimer) {
      clearInterval(existingTimer);
      this.trainingTypewriterTimers.delete(message.id);
    }

    message.renderedText = '';
    message.isTyping = true;

    let index = 0;
    const delay = this.getTrainingTypewriterDelay(fullText.length);
    const timer = setInterval(() => {
      index = Math.min(index + 1, fullText.length);
      message.renderedText = fullText.slice(0, index);

      if (index >= fullText.length) {
        clearInterval(timer);
        this.trainingTypewriterTimers.delete(message.id);
        message.isTyping = false;
      }

      this.cdr.detectChanges();
    }, delay);

    this.trainingTypewriterTimers.set(message.id, timer);
  }

  private getTrainingTypewriterDelay(length: number): number {
    if (length > 1200) {
      return 2;
    }

    if (length > 500) {
      return 3;
    }

    if (length > 180) {
      return 5;
    }

    return 8;
  }

  getRenderedTrainingMessageText(message: TrainingChatMessage): string {
    return message.renderedText || '';
  }

  private resolveTrainingImageFileName(mimeType: string): string {
    const baseName = this.trainingContextAttachment?.fileName || `page-${this.currentPage || 1}`;
    const safeBaseName = baseName.replace(/\.[^.]+$/, '');
    const ext = mimeType === 'image/jpeg' ? 'jpg' : 'png';
    return `${safeBaseName}.${ext}`;
  }

  private async ensureTrainingImagePngOrJpeg(imageBlob: Blob | null): Promise<Blob | null> {
    if (!imageBlob) {
      return null;
    }

    if (imageBlob.type === 'image/png' || imageBlob.type === 'image/jpeg') {
      return imageBlob;
    }

    return this.convertBlobToPng(imageBlob);
  }

  private convertBlobToPng(blob: Blob): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(blob);

      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.naturalWidth || image.width;
          canvas.height = image.naturalHeight || image.height;

          const context = canvas.getContext('2d');
          if (!context) {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Unable to convert image to PNG.'));
            return;
          }

          context.drawImage(image, 0, 0);
          canvas.toBlob(converted => {
            URL.revokeObjectURL(objectUrl);
            if (!converted) {
              reject(new Error('Converted PNG image is empty.'));
              return;
            }

            resolve(converted);
          }, 'image/png');
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          reject(error instanceof Error ? error : new Error('Image conversion failed.'));
        }
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unsupported image format for conversion.'));
      };

      image.src = objectUrl;
    });
  }

  openTrainingResponseDetails(message: TrainingChatMessage): void {
    const detailsList = this.resolveTrainingReadyDetailsList(message);
    if (detailsList.length === 0) {
      return;
    }

    this.trainingSelectedReadyDetailsList = this.cloneTrainingReadyDetailsList(detailsList);
    this.trainingOriginalReadyDetailsList = this.cloneTrainingReadyDetailsList(detailsList);
    this.trainingSelectedReadyDetails = this.trainingSelectedReadyDetailsList[0] || null;
    this.isTrainingResponseModalOpen = true;
  }

  closeTrainingResponseDetails(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeTrainingFieldPopup();
    this.isTrainingResponseModalOpen = false;
    this.trainingSelectedReadyDetails = null;
    this.trainingSelectedReadyDetailsList = [];
    this.trainingOriginalReadyDetailsList = [];
  }

  onTrainingResponseDetailsChanged(): void {
    this.trainingSelectedReadyDetails = this.trainingSelectedReadyDetailsList[0] || null;
  }

  getTrainingFeatureOptions(fieldType: string): string[] {
    const normalizedType = String(fieldType || '').trim().toLowerCase();
    if (normalizedType === 'array') {
      return this.trainingArrayFeatureOptions;
    }

    return this.trainingSimpleFeatureOptions;
  }

  getTrainingFieldTypeLabel(fieldType: string): string {
    const normalizedType = String(fieldType || '').trim().toLowerCase();
    return normalizedType === 'normal' ? 'Simple' : fieldType;
  }

  onTrainingReadyDetailTypeChanged(detail: TrainingReadyDetailsView): void {
    const options = this.getTrainingFeatureOptions(detail.fieldType);
    if (options.length === 0) {
      detail.feature = '';
      this.onTrainingResponseDetailsChanged();
      return;
    }

    const currentFeature = String(detail.feature || '').trim();
    if (currentFeature && !options.includes(currentFeature)) {
      detail.feature = '';
    }

    this.onTrainingResponseDetailsChanged();
  }

  isTrainingModalFieldNameDuplicate(index: number): boolean {
    const current = this.trainingSelectedReadyDetailsList[index];
    if (!current) {
      return false;
    }

    const currentName = String(current.fieldName || '').trim().toLowerCase();
    if (!currentName) {
      return false;
    }

    return this.trainingSelectedReadyDetailsList.some((detail, detailIndex) => {
      if (detailIndex === index) {
        return false;
      }

      return String(detail.fieldName || '').trim().toLowerCase() === currentName;
    });
  }

  isTrainingModalFieldNameAlreadyExists(index: number): boolean {
    const current = this.trainingSelectedReadyDetailsList[index];
    if (!current) {
      return false;
    }

    const currentName = String(current.fieldName || '').trim().toLowerCase();
    if (!currentName) {
      return false;
    }

    return this.fieldNames.some(fieldName => String(fieldName || '').trim().toLowerCase() === currentName);
  }

  isTrainingModalFieldNameConflict(index: number): boolean {
    return this.isTrainingModalFieldNameDuplicate(index) || this.isTrainingModalFieldNameAlreadyExists(index);
  }

  isTrainingRequiredColumn(fieldType: string, column: TrainingRequiredDetailColumn): boolean {
    if (column === 'fieldName' || column === 'fieldType' || column === 'feature') {
      return true;
    }

    const normalizedType = String(fieldType || '').trim().toLowerCase();
    switch (normalizedType) {
      case 'normal':
        return column === 'prompt';
      case 'array':
        return column === 'prompt' || column === 'additionalPrompt';
      case 'complex':
        return column === 'fieldSchema';
      case 'compute':
        return column === 'computeSchema';
      default:
        return false;
    }
  }

  isTrainingModalRequiredFieldEmpty(detail: TrainingReadyDetailsView, column: TrainingRequiredDetailColumn): boolean {
    if (!detail || !this.isTrainingRequiredColumn(detail.fieldType, column)) {
      return false;
    }

    const value = (() => {
      switch (column) {
        case 'fieldName':
          return detail.fieldName;
        case 'fieldType':
          return detail.fieldType;
        case 'feature':
          return detail.feature;
        case 'prompt':
          return detail.prompt;
        case 'additionalPrompt':
          return detail.additionalPrompt;
        case 'fieldSchema':
          return detail.fieldSchema;
        case 'computeSchema':
          return detail.computeSchema;
        default:
          return '';
      }
    })();

    return !this.normalizeTrainingDetailValue(value);
  }

  isTrainingModalFieldReadOnly(
    detail: TrainingReadyDetailsView,
    column: TrainingEditableDetailColumn
  ): boolean {
    const fieldType = String(detail?.fieldType || '').trim().toLowerCase();

    if (fieldType === 'normal') {
      return column === 'additionalPrompt' || column === 'fieldSchema' || column === 'computeSchema';
    }

    if (fieldType === 'array') {
      return column === 'fieldSchema' || column === 'computeSchema';
    }

    return false;
  }

  openTrainingFieldPopup(index: number, column: TrainingEditableDetailColumn): void {
    const detail = this.trainingSelectedReadyDetailsList[index];
    if (!detail || this.isTrainingModalFieldReadOnly(detail, column)) {
      return;
    }

    const currentValue = this.getTrainingDetailColumnValue(detail, column);
    this.trainingFieldPopupRowIndex = index;
    this.trainingFieldPopupColumn = column;
    this.trainingFieldPopupInitialValue = currentValue;
    this.trainingFieldPopupDraftValue = currentValue;
    this.trainingFieldPopupError = '';
    this.trainingFieldPopupUploadedFieldSchemaValue = null;
    this.trainingFieldPopupUploadedComputeSchemaValue = null;
    this.isTrainingFieldPopupJsonUploading = false;
    this.trainingFieldPopupJsonFileSelected = false;
    this.isTrainingFieldPopupOpen = true;
  }

  closeTrainingFieldPopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.isTrainingFieldPopupOpen = false;
    this.trainingFieldPopupRowIndex = -1;
    this.trainingFieldPopupColumn = null;
    this.trainingFieldPopupInitialValue = '';
    this.trainingFieldPopupDraftValue = '';
    this.trainingFieldPopupError = '';
    this.trainingFieldPopupUploadedFieldSchemaValue = null;
    this.trainingFieldPopupUploadedComputeSchemaValue = null;
    this.isTrainingFieldPopupJsonUploading = false;
    this.trainingFieldPopupJsonFileSelected = false;
  }

  undoTrainingFieldPopupValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isTrainingFieldPopupOpen) {
      return;
    }

    this.trainingFieldPopupDraftValue = this.trainingFieldPopupInitialValue;
    this.trainingFieldPopupError = '';
    this.trainingFieldPopupUploadedFieldSchemaValue = null;
    this.trainingFieldPopupUploadedComputeSchemaValue = null;
  }

  clearTrainingFieldPopupValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isTrainingFieldPopupOpen) {
      return;
    }

    this.trainingFieldPopupDraftValue = '';
    this.trainingFieldPopupUploadedFieldSchemaValue = null;
    this.trainingFieldPopupUploadedComputeSchemaValue = null;
    this.onTrainingFieldPopupDraftChanged();
  }

  copyTrainingFieldPopupValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isTrainingFieldPopupOpen) {
      return;
    }

    const value = this.trainingFieldPopupDraftValue;
    if (!String(value || '').trim()) {
      this.notificationService.showError('Nothing to copy — field is empty');
      return;
    }

    navigator.clipboard.writeText(value)
      .then(() => this.notificationService.showSuccess('Copied to clipboard'))
      .catch(() => this.notificationService.showError('Failed to copy to clipboard'));
  }

  startTrainingFieldPopupJsonUpload(fileInput: HTMLInputElement, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.trainingFieldPopupJsonFileSelected = false;
    this.isTrainingFieldPopupJsonUploading = true;
    this.cdr.detectChanges();
    fileInput.click();

    const onWindowFocus = () => {
      setTimeout(() => {
        if (!this.trainingFieldPopupJsonFileSelected) {
          this.isTrainingFieldPopupJsonUploading = false;
          this.cdr.detectChanges();
        }
      }, 300);
    };

    window.addEventListener('focus', onWindowFocus, { once: true });
  }

  handleTrainingFieldPopupJsonUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.isTrainingFieldPopupJsonUploading = false;
      this.cdr.detectChanges();
      return;
    }

    this.trainingFieldPopupJsonFileSelected = true;
    const file = input.files[0];

    if (!file.name.endsWith('.json')) {
      this.notificationService.showError('Please select a valid JSON file');
      input.value = '';
      this.isTrainingFieldPopupJsonUploading = false;
      this.cdr.detectChanges();
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.notificationService.showError('File size must be less than 5MB');
      input.value = '';
      this.isTrainingFieldPopupJsonUploading = false;
      this.cdr.detectChanges();
      return;
    }

    const stopUploading = () => {
      setTimeout(() => {
        this.isTrainingFieldPopupJsonUploading = false;
        this.cdr.detectChanges();
      }, 500);
    };

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);

        const allowedKeys = ['extraction schema', 'compute schema'];
        const parsedKeys = Object.keys(parsed || {});
        const invalidKeys = parsedKeys.filter(key => !allowedKeys.includes(key));
        if (invalidKeys.length > 0 || (!parsed['extraction schema'] && !parsed['compute schema'])) {
          this.notificationService.showError('Invalid JSON — only "extraction schema" and "compute schema" keys are allowed');
          input.value = '';
          stopUploading();
          return;
        }

        const detail = this.trainingSelectedReadyDetailsList[this.trainingFieldPopupRowIndex];
        const fieldType = String(detail?.fieldType || '').trim().toLowerCase();
        const isComputeType = fieldType === 'compute';

        this.trainingFieldPopupUploadedFieldSchemaValue = null;
        this.trainingFieldPopupUploadedComputeSchemaValue = null;

        if (parsed['extraction schema'] && !isComputeType) {
          this.trainingFieldPopupUploadedFieldSchemaValue = JSON.stringify(parsed['extraction schema'], null, 2);
          if (this.trainingFieldPopupColumn === 'fieldSchema') {
            this.trainingFieldPopupDraftValue = this.trainingFieldPopupUploadedFieldSchemaValue;
          }
        }

        if (parsed['compute schema']) {
          this.trainingFieldPopupUploadedComputeSchemaValue = JSON.stringify(parsed['compute schema'], null, 2);
          if (this.trainingFieldPopupColumn === 'computeSchema') {
            this.trainingFieldPopupDraftValue = this.trainingFieldPopupUploadedComputeSchemaValue;
          }
        }

        this.onTrainingFieldPopupDraftChanged();
        stopUploading();
      } catch {
        this.notificationService.showError('Invalid JSON file. Please upload a valid JSON file.');
        input.value = '';
        stopUploading();
      }
    };

    reader.onerror = () => {
      this.notificationService.showError('Failed to read the file.');
      input.value = '';
      stopUploading();
    };

    reader.readAsText(file);
  }

  onTrainingFieldPopupDraftChanged(): void {
    if (!this.isTrainingFieldPopupSchemaColumn) {
      this.trainingFieldPopupError = '';
      return;
    }

    const rawValue = this.normalizeTrainingDetailValue(this.trainingFieldPopupDraftValue);
    if (!rawValue) {
      this.trainingFieldPopupError = '';
      return;
    }

    if (!this.trainingFieldPopupColumnLabel) {
      this.trainingFieldPopupError = '';
      return;
    }

    try {
      this.parseTrainingJsonField(rawValue, this.trainingFieldPopupColumnLabel);
      this.trainingFieldPopupError = '';
    } catch (error: any) {
      this.trainingFieldPopupError = String(error?.message || `${this.trainingFieldPopupColumnLabel} must be valid JSON.`);
    }
  }

  applyTrainingFieldPopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.isTrainingFieldPopupOpen || this.trainingFieldPopupRowIndex < 0 || !this.trainingFieldPopupColumn) {
      return;
    }

    this.onTrainingFieldPopupDraftChanged();
    if (this.trainingFieldPopupError) {
      return;
    }

    const detail = this.trainingSelectedReadyDetailsList[this.trainingFieldPopupRowIndex];
    if (!detail) {
      this.closeTrainingFieldPopup();
      return;
    }

    this.setTrainingDetailColumnValue(detail, this.trainingFieldPopupColumn, this.trainingFieldPopupDraftValue);

    if (this.trainingFieldPopupUploadedFieldSchemaValue !== null) {
      detail.fieldSchema = this.trainingFieldPopupUploadedFieldSchemaValue;
    }

    if (this.trainingFieldPopupUploadedComputeSchemaValue !== null) {
      detail.computeSchema = this.trainingFieldPopupUploadedComputeSchemaValue;
    }

    this.onTrainingResponseDetailsChanged();
    this.closeTrainingFieldPopup();
  }

  private getTrainingDetailColumnValue(detail: TrainingReadyDetailsView, column: TrainingEditableDetailColumn): string {
    const value = detail[column];
    return value === '—' ? '' : String(value ?? '');
  }

  private setTrainingDetailColumnValue(detail: TrainingReadyDetailsView, column: TrainingEditableDetailColumn, value: string): void {
    detail[column] = value;
  }

  async continueTrainingResponseDetails(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isTrainingResponseContinueDisabled) {
      return;
    }

    if (!this.trainingContextAttachment) {
      this.useCurrentPreviewContext();
    }

    const contextLabel = this.trainingContextAttachment?.label || 'Current page context';
    const lockedDetails = this.cloneTrainingReadyDetailsList(this.trainingSelectedReadyDetailsList);
    const detailsPayload = this.buildTrainingEditedDetailPayload(lockedDetails);
    this.trainingPendingLockedDetailsForContinue = lockedDetails;

    this.ensureTrainingSystemMessage();
    this.trainingApiMessages.push({
      role: 'user',
      content: this.buildContinueLockedInstruction(detailsPayload)
    });
    this.appendTrainingMessage('user', 'Continue with edited field details.', contextLabel);

    this.closeTrainingResponseDetails();
    await this.dispatchTrainingAiSuggestRequest(contextLabel);
  }

  openTrainingContextModal(): void {
    if (!this.selectedFile) {
      return;
    }

    this.trainingContextDraftInput = this.trainingDescriptionInput;
    this.isTrainingContextModalOpen = true;
  }

  closeTrainingContextModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isTrainingContextModalOpen = false;
    this.trainingContextDraftInput = '';
  }

  saveTrainingContextModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.trainingDescriptionInput = this.trainingContextDraftInput.trim();
    this.isTrainingContextModalOpen = false;
    this.trainingContextDraftInput = '';
  }

  private cloneTrainingReadyDetailsList(detailsList: TrainingReadyDetailsView[]): TrainingReadyDetailsView[] {
    return detailsList.map(detail => ({ ...detail }));
  }

  private hasTrainingResponseDetailsChanged(): boolean {
    if (this.trainingSelectedReadyDetailsList.length !== this.trainingOriginalReadyDetailsList.length) {
      return true;
    }

    return this.trainingSelectedReadyDetailsList.some((current, index) => {
      const original = this.trainingOriginalReadyDetailsList[index];
      if (!original) {
        return true;
      }

      return this.normalizeTrainingDetailValue(current.fieldName) !== this.normalizeTrainingDetailValue(original.fieldName) ||
        this.normalizeTrainingDetailValue(current.fieldType) !== this.normalizeTrainingDetailValue(original.fieldType) ||
        this.normalizeTrainingDetailValue(current.feature) !== this.normalizeTrainingDetailValue(original.feature) ||
        this.normalizeTrainingDetailValue(current.prompt) !== this.normalizeTrainingDetailValue(original.prompt) ||
        this.normalizeTrainingDetailValue(current.additionalPrompt) !== this.normalizeTrainingDetailValue(original.additionalPrompt) ||
        this.normalizeTrainingDetailValue(current.fieldSchema) !== this.normalizeTrainingDetailValue(original.fieldSchema) ||
        this.normalizeTrainingDetailValue(current.computeSchema) !== this.normalizeTrainingDetailValue(original.computeSchema) ||
        this.normalizeTrainingDetailValue(current.externalKey) !== this.normalizeTrainingDetailValue(original.externalKey);
    });
  }

  private normalizeTrainingDetailValue(value: string | null | undefined): string {
    const normalized = String(value ?? '').trim();
    return normalized === '—' ? '' : normalized;
  }

  private formatTrainingScreenshotLabel(date: Date = new Date()): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear());
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const hour24 = date.getHours();
    const meridiem = hour24 >= 12 ? 'pm' : 'am';
    const hour12 = hour24 % 12 || 12;
    const hour = String(hour12).padStart(2, '0');

    return `Screenshot-${month}-${day}-${year}-${hour}:${minutes} ${meridiem}`;
  }

  private resolveTrainingLockedReadyDetails(aiReadyDetails: TrainingReadyDetailsView[]): TrainingReadyDetailsView[] {
    const lockedDetails = this.trainingPendingLockedDetailsForContinue;
    if (!lockedDetails || lockedDetails.length === 0) {
      return aiReadyDetails;
    }

    // Keep AI turn semantics, but preserve edited field values as authoritative.
    return lockedDetails.map(detail => ({ ...detail }));
  }

  private clearTrainingContinueLockedState(): void {
    this.trainingPendingLockedDetailsForContinue = null;
  }

  private buildTrainingEditedDetailPayload(detailsList: TrainingReadyDetailsView[]): TrainingEditedDetailPayload[] {
    return detailsList.map(detail => ({
      field_name: String(detail.fieldName ?? ''),
      field_type: String(detail.fieldType ?? ''),
      feature: String(detail.feature ?? ''),
      prompt: String(detail.prompt ?? ''),
      additional_prompt: String(detail.additionalPrompt ?? ''),
      field_schema: String(detail.fieldSchema ?? ''),
      compute_schema: String(detail.computeSchema ?? ''),
      external_key: String(detail.externalKey ?? '')
    }));
  }

  private buildContinueLockedInstruction(detailsPayload: TrainingEditedDetailPayload[]): string {
    return [
      'Continue training with these edited field details.',
      'Treat every provided value as locked and authoritative.',
      'Do not rename fields and do not alter field type, feature, prompt, additional prompt, field schema, compute schema, or external key.',
      'If clarification is required, ask concise questions while preserving all provided values.',
      'Return ready output aligned to these exact edited values.',
      `edited_fields_json=${JSON.stringify(detailsPayload)}`
    ].join('\n');
  }

  private getLatestReadyTrainingMessage(): TrainingChatMessage | null {
    for (let index = this.trainingMessages.length - 1; index >= 0; index--) {
      const message = this.trainingMessages[index];
      if (this.resolveTrainingReadyDetailsList(message).length > 0) {
        return message;
      }
    }

    return null;
  }

  private getLatestReadyDetailsList(): TrainingReadyDetailsView[] {
    const latestReadyMessage = this.getLatestReadyTrainingMessage();
    if (!latestReadyMessage) {
      return [];
    }

    return this.resolveTrainingReadyDetailsList(latestReadyMessage);
  }

  private resolveTrainingReadyDetailsList(message: TrainingChatMessage | null | undefined): TrainingReadyDetailsView[] {
    if (!message) {
      return [];
    }

    if (Array.isArray(message.readyDetailsList) && message.readyDetailsList.length > 0) {
      return message.readyDetailsList;
    }

    if (message.readyDetails) {
      return [message.readyDetails];
    }

    return [];
  }

  private buildTrainingReadyDetailsList(rawPayload: any, rawResponse: string): TrainingReadyDetailsView[] {
    const payload = rawPayload && typeof rawPayload === 'object' ? rawPayload : {};
    const rawFields = Array.isArray(payload.fields)
      ? payload.fields
      : (Array.isArray(payload.generated_fields) ? payload.generated_fields : []);

    if (rawFields.length > 0) {
      return rawFields
        .map((field: any) => this.buildTrainingReadyDetailsView(field, rawResponse))
        .filter((details: TrainingReadyDetailsView) => !!details.fieldName && details.fieldName !== '—');
    }

    const single = this.buildTrainingReadyDetailsView(payload, rawResponse);
    return single.fieldName && single.fieldName !== '—' ? [single] : [];
  }

  private buildTrainingReadyDetailsView(rawPayload: any, rawResponse: string): TrainingReadyDetailsView {
    const payload = rawPayload && typeof rawPayload === 'object' ? rawPayload : {};
    const fieldSchema = payload.field_schema && typeof payload.field_schema === 'object' ? payload.field_schema : null;
    const computeSchema = payload.computed_schema || payload.compute_schema || null;

    const fieldType = this.resolveTrainingFieldType(fieldSchema, computeSchema);
    const fieldName = String(payload.field_name || '—');
    const featureRaw = String(payload.feature || fieldSchema?.ld_feature || fieldSchema?.items?.ld_feature || '');
    const feature = featureRaw
      ? featureRaw.charAt(0).toUpperCase() + featureRaw.slice(1).toLowerCase()
      : '—';

    const prompt = fieldSchema?.type === 'array'
      ? String(fieldSchema?.items?.description || '')
      : String(fieldSchema?.description || '');
    const additionalPrompt = fieldSchema?.type === 'array'
      ? String(fieldSchema?.description || '')
      : '';
    const externalKey = fieldSchema?.type === 'array'
      ? String(fieldSchema?.items?.external_key || fieldSchema?.external_key || payload.external_key || '')
      : String(fieldSchema?.external_key || payload.external_key || '');
    const showFieldSchema = fieldType === 'Complex' || fieldType === 'Compute';

    return {
      rawResponse,
      fieldName: fieldName || '—',
      fieldType: fieldType === 'Simple' ? 'Normal' : fieldType,
      feature,
      prompt: prompt || '—',
      additionalPrompt: additionalPrompt || '—',
      fieldSchema: showFieldSchema && fieldSchema ? JSON.stringify(fieldSchema, null, 2) : '—',
      computeSchema: computeSchema ? JSON.stringify(computeSchema, null, 2) : '—',
      externalKey: externalKey || '—'
    };
  }

  private resolveTrainingFieldType(fieldSchema: any, computeSchema: any): string {
    if (computeSchema && (!fieldSchema || Object.keys(fieldSchema).length === 0)) {
      return 'Compute';
    }

    if (!fieldSchema || typeof fieldSchema !== 'object') {
      return 'Simple';
    }

    // If schema explicitly declares array, honor it unless compute-only payload is present.
    if (fieldSchema.type === 'array' && !computeSchema) {
      return 'Array';
    }

    const allowedTopKeys = new Set(['description', 'type', 'ld_feature', 'items', 'external_key']);
    const allowedItemsKeys = new Set(['description', 'ld_feature', 'external_key', 'type']);
    const schemaKeys = Object.keys(fieldSchema);
    const hasExtraKeys = schemaKeys.some(key => !allowedTopKeys.has(key));
    const hasExtraItemKeys = fieldSchema.items && typeof fieldSchema.items === 'object'
      ? Object.keys(fieldSchema.items).some((key: string) => !allowedItemsKeys.has(key))
      : false;

    if (computeSchema || hasExtraKeys || hasExtraItemKeys) {
      return 'Complex';
    }

    if (fieldSchema.type === 'array') {
      return 'Array';
    }

    return 'Simple';
  }

  handleTrainingGenerate(): void {
    if (!this.trainingCanGenerate) {
      this.notificationService.showError('Generate will be enabled when AI status is ready.');
      return;
    }

    const latestReadyMessage = this.getLatestReadyTrainingMessage();
    if (!latestReadyMessage || this.resolveTrainingReadyDetailsList(latestReadyMessage).length === 0) {
      this.notificationService.showError('Ready response details are not available yet.');
      return;
    }

    this.openTrainingResponseDetails(latestReadyMessage);
  }

  confirmTrainingResponseGenerate(): void {
    if (this.isTrainingGenerateActionDisabled) {
      return;
    }

    const selectedDetailsList = this.trainingSelectedReadyDetailsList.length > 0
      ? this.trainingSelectedReadyDetailsList
      : this.getLatestReadyDetailsList();
    if (selectedDetailsList.length === 0) {
      this.notificationService.showError('Ready response details are not available yet.');
      return;
    }

    const details = selectedDetailsList[0];

    if (!this.documentModel) {
      this.notificationService.showError('Document model is not available for Generate action.');
      return;
    }

    const draftPayload: TrainingGenerateDraftPayload = {
      documentModel: this.documentModel,
      createdAt: Date.now(),
      returnUrl: this.router.url,
      autoCreate: true,
      fields: selectedDetailsList.map(detail => ({
        fieldName: detail.fieldName,
        fieldType: detail.fieldType,
        feature: detail.feature,
        prompt: detail.prompt,
        additionalPrompt: detail.additionalPrompt,
        fieldSchema: detail.fieldSchema,
        computeSchema: detail.computeSchema,
        externalKey: detail.externalKey
      })),
      fieldName: details.fieldName,
      fieldType: details.fieldType,
      feature: details.feature,
      prompt: details.prompt,
      additionalPrompt: details.additionalPrompt,
      fieldSchema: details.fieldSchema,
      computeSchema: details.computeSchema,
      externalKey: details.externalKey
    };

    try {
      sessionStorage.setItem(this.trainingGenerateDraftStorageKey, JSON.stringify(draftPayload));
    } catch (error) {
      this.notificationService.showError('Unable to continue to fields page right now. Please try again.');
      return;
    }

    this.closeTrainingResponseDetails();
    this.router.navigate(['/edit-document-model', this.documentModel], {
      queryParams: {
        from: 'prompt-tuning',
        returnUrl: this.router.url
      }
    });
  }

  generateTrainingFieldDirectly(): void {
    if (this.isTrainingGenerateActionDisabled) {
      return;
    }

    if (this.hasTrainingResponseFieldNameConflicts) {
      this.notificationService.showError('A field with this name already exists');
      return;
    }

    const selectedDetailsList = this.trainingSelectedReadyDetailsList.length > 0
      ? this.trainingSelectedReadyDetailsList
      : this.getLatestReadyDetailsList();
    if (selectedDetailsList.length === 0) {
      this.notificationService.showError('Ready response details are not available yet.');
      return;
    }

    if (!this.documentModel) {
      this.notificationService.showError('Document model is not available for Generate action.');
      return;
    }

    const detail = selectedDetailsList[0];
    const normalizedFieldName = this.normalizeTrainingDetailValue(detail.fieldName);
    if (!normalizedFieldName) {
      this.notificationService.showError('Field name is required before generating.');
      return;
    }

    let schemaPayload: { fieldSchema: any; computedSchema: any };
    try {
      schemaPayload = this.buildTrainingCreateSchemaPayload(detail);
    } catch (error: any) {
      const message = String(error?.message || 'Invalid field schema values.');
      this.notificationService.showError(message);
      return;
    }

    this.isTrainingGeneratingDirectly = true;
    this.documentModelService.createField(this.documentModel, normalizedFieldName, {
      field_name: normalizedFieldName,
      document_model: this.documentModel,
      field_schema: schemaPayload.fieldSchema,
      computed_schema: schemaPayload.computedSchema,
      is_internal: false
    }).subscribe({
      next: () => {
        if (!this.fieldNames.some(fieldName => String(fieldName || '').trim().toLowerCase() === normalizedFieldName.toLowerCase())) {
          this.fieldNames = [...this.fieldNames, normalizedFieldName];
        }

        this.truthManagementService.invalidateDocumentModelCache(this.documentModel);
        this.notificationService.showSuccess(`Field "${normalizedFieldName}" created successfully`);
        this.closeTrainingResponseDetails();
        this.isTrainingGeneratingDirectly = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        const msg = error?.error?.detail || error?.error?.message || (typeof error?.error === 'string' ? error.error : error?.message || 'Unknown error');
        this.notificationService.showError(`Failed to create field: ${msg}`);
        this.isTrainingGeneratingDirectly = false;
        this.cdr.detectChanges();
      }
    });
  }

  private buildTrainingCreateSchemaPayload(detail: TrainingReadyDetailsView): { fieldSchema: any; computedSchema: any } {
    const fieldType = String(detail.fieldType || '').trim().toLowerCase();
    const normalizedFeature = this.normalizeTrainingDetailValue(detail.feature).toLowerCase();
    const hasFeature = normalizedFeature.length > 0 && normalizedFeature !== 'normal';
    const normalizedExternalKey = this.normalizeTrainingDetailValue(detail.externalKey);
    const hasExternalKey = normalizedExternalKey.length > 0;

    let fieldSchema: any = null;
    switch (fieldType) {
      case 'normal': {
        fieldSchema = {
          description: this.normalizeTrainingDetailValue(detail.prompt)
        };
        if (hasFeature) {
          fieldSchema.ld_feature = normalizedFeature;
        }
        if (hasExternalKey) {
          fieldSchema.external_key = normalizedExternalKey;
        }
        break;
      }
      case 'array': {
        const items: any = {
          description: this.normalizeTrainingDetailValue(detail.prompt)
        };
        if (hasExternalKey) {
          items.external_key = normalizedExternalKey;
        }

        fieldSchema = {
          type: 'array',
          items,
          description: this.normalizeTrainingDetailValue(detail.additionalPrompt)
        };

        if (hasFeature) {
          fieldSchema.ld_feature = normalizedFeature;
        }
        break;
      }
      case 'complex': {
        const rawFieldSchema = this.normalizeTrainingDetailValue(detail.fieldSchema);
        fieldSchema = rawFieldSchema ? this.parseTrainingJsonField(rawFieldSchema, 'Field Schema') : null;
        break;
      }
      case 'compute':
        fieldSchema = null;
        break;
      default: {
        fieldSchema = {
          description: this.normalizeTrainingDetailValue(detail.prompt)
        };
        if (hasFeature) {
          fieldSchema.ld_feature = normalizedFeature;
        }
        if (hasExternalKey) {
          fieldSchema.external_key = normalizedExternalKey;
        }
        break;
      }
    }

    const isComplexOrCompute = fieldType === 'complex' || fieldType === 'compute';
    const rawComputeSchema = this.normalizeTrainingDetailValue(detail.computeSchema);
    const computedSchema = isComplexOrCompute && rawComputeSchema
      ? this.parseTrainingJsonField(rawComputeSchema, 'Compute Schema')
      : null;

    return {
      fieldSchema,
      computedSchema
    };
  }

  private parseTrainingJsonField(rawValue: string, fieldLabel: string): any {
    const normalized = rawValue.trim();
    if (!normalized) {
      return null;
    }

    try {
      return JSON.parse(normalized);
    } catch {
      try {
        return JSON.parse(`{${normalized}}`);
      } catch {
        throw new Error(`${fieldLabel} must be valid JSON before generating.`);
      }
    }
  }

  /**
   * Save current file state to sessionStorage
   */
  private saveCurrentFileState(): void {
    if (!this.selectedFile) return;
    
    if (this.selectedFieldName || this.revisedPrompt) {
      const state = {
        fieldName: this.selectedFieldName,
        currentPrompt: this.currentPrompt,
        revisedPrompt: this.revisedPrompt,
        originalRevisedPrompt: this.originalRevisedPrompt
      };
      
      try {
        const allStates = JSON.parse(sessionStorage.getItem('promptTuningStates') || '{}');
        allStates[this.selectedFile.id] = state;
        sessionStorage.setItem('promptTuningStates', JSON.stringify(allStates));
      } catch (error) {
      }
    }
  }

  /**
   * Restore file state from sessionStorage
   */
  private restoreFileState(fileId: number | string): void {
    try {
      const allStates = JSON.parse(sessionStorage.getItem('promptTuningStates') || '{}');
      const savedState = allStates[fileId];
      
      if (savedState) {
        this.selectedFieldName = savedState.fieldName || '';
        this.fieldNameInput = savedState.fieldName || '';
        this.currentPrompt = savedState.currentPrompt || '';
        this.revisedPrompt = savedState.revisedPrompt || '';
        this.originalRevisedPrompt = savedState.originalRevisedPrompt || '';
      } else {
        this.clearFormState();
      }
    } catch (error) {
      this.clearFormState();
    }
  }

  /**
   * Clear form state
   */
  private clearFormState(): void {
    this.selectedFieldName = '';
    this.fieldNameInput = '';
    this.currentPrompt = '';
    this.revisedPrompt = '';
    this.originalRevisedPrompt = '';
  }

  /**
   * Clear saved state from sessionStorage
   */
  private clearSavedState(): void {
    if (!this.selectedFile) return;
    
    try {
      const allStates = JSON.parse(sessionStorage.getItem('promptTuningStates') || '{}');
      delete allStates[this.selectedFile.id];
      sessionStorage.setItem('promptTuningStates', JSON.stringify(allStates));
    } catch (error) {
    }
  }

  /**
   * Navigation guard - check for unsaved changes
   */
  canDeactivate(): Promise<boolean> | boolean {
    try {
      const allStates = JSON.parse(sessionStorage.getItem('promptTuningStates') || '{}');
      const hasAnyUnsavedChanges = Object.keys(allStates).length > 0 || this.hasPromptChanges();
      
      if (hasAnyUnsavedChanges) {
        return new Promise<boolean>((resolve) => {
          // Show options: Save & Leave / Don't Save / Cancel
          this.modalService.showConfirmation(
            'You have unsaved changes. What would you like to do?',
            () => {
              // User clicked "Save & Leave" - save then leave
              // If no prompt changes, just leave
              if (!this.hasPromptChanges()) {
                this.clearAllStates();
                resolve(true);
                return;
              }
              
              // Show reason dialog
              this.modalService.showPrompt(
                'Please enter the reason for this change (optional):',
                (changeReason: string) => {
                  // Allow empty reason - user can save with null/empty if they want
                  
                  // Perform save and then navigate away
                  const fieldSchema = this.fieldSchemaMap.get(this.selectedFieldName);
                  if (!fieldSchema) {
                    this.notificationService.showError('Field schema not found.');
                    resolve(false);
                    return;
                  }
                  
                  const updatedFieldSchema: any = {
                    type: fieldSchema.field_schema.type
                  };
                  
                  if (fieldSchema.field_schema.items) {
                    updatedFieldSchema.items = { description: this.revisedPrompt };
                    updatedFieldSchema.description = fieldSchema.field_schema.description;
                  } else {
                    updatedFieldSchema.description = this.revisedPrompt;
                  }
                  
                  this.truthManagementService.updateFieldSchema(
                    this.documentModel,
                    this.selectedFieldName,
                    changeReason.trim() || '', // Allow empty string
                    updatedFieldSchema
                  ).subscribe({
                    next: () => {
                      this.clearAllStates();
                      resolve(true); // Allow navigation
                    },
                    error: (err) => {
                      this.notificationService.showError('Failed to save. Please try again.');
                      resolve(false); // Stay on page
                    }
                  });
                },
                {
                  title: 'Change Reason',
                  placeholder: 'Describe what changed and why (optional)...',
                  confirmText: 'Save & Leave',
                  cancelText: 'Cancel'
                }
              );
            },
            {
              title: 'Unsaved Changes',
              confirmText: 'Save & Leave',
              cancelText: 'Stay on Page'
            }
          );
          
          // Add "Don't Save" button using setTimeout to modify modal config
          setTimeout(() => {
            const container = document.querySelector('.modal-footer-confirmation');
            if (container && !document.getElementById('dont-save-btn')) {
              const dontSaveBtn = document.createElement('button');
              dontSaveBtn.id = 'dont-save-btn';
              dontSaveBtn.className = 'modal-btn modal-btn-cancel';
              dontSaveBtn.textContent = "Don't Save";
              dontSaveBtn.type = 'button';
              // Ensure styles are applied properly
              dontSaveBtn.style.background = '#f1f5f9';
              dontSaveBtn.style.color = '#475569';
              dontSaveBtn.style.border = '1px solid #cbd5e1';
              dontSaveBtn.style.padding = '0.625rem 1.5rem';
              dontSaveBtn.style.borderRadius = '8px';
              dontSaveBtn.style.fontSize = '0.875rem'; /* Reduced from 0.9375rem */
              dontSaveBtn.style.fontWeight = '600';
              dontSaveBtn.style.cursor = 'pointer';
              dontSaveBtn.style.minWidth = '100px';
              dontSaveBtn.style.whiteSpace = 'nowrap'; /* Prevent text wrapping */
              dontSaveBtn.style.transition = 'all 0.2s';
              dontSaveBtn.onmouseover = () => {
                dontSaveBtn.style.background = '#e2e8f0';
                dontSaveBtn.style.borderColor = '#94a3b8';
              };
              dontSaveBtn.onmouseout = () => {
                dontSaveBtn.style.background = '#f1f5f9';
                dontSaveBtn.style.borderColor = '#cbd5e1';
              };
              dontSaveBtn.onclick = () => {
                this.clearAllStates();
                this.modalService.close();
                resolve(true); // Allow navigation
              };
              // Insert before the first button (Stay on Page)
              container.insertBefore(dontSaveBtn, container.firstChild);
            }
          }, 0);
        });
      }
    } catch (error) {
    }
    
    return true;
  }

  /**
   * Clear all component states and sessionStorage
   */
  private clearAllStates(): void {
    // Clear component state
    this.currentPrompt = '';
    this.revisedPrompt = '';
    this.originalRevisedPrompt = '';
    this.selectedFieldName = '';
    this.showVersionsDropdown = false;
    this.versionHistory = [];
    
    // Clear sessionStorage
    sessionStorage.removeItem('promptTuningStates');
    sessionStorage.removeItem(`promptTuning_${this.documentModel}`);
  }
}
